import { NgView } from '@app/di/decorators';
import { Token } from '@app/di/token';
import { ClaraViewer, IClaraSceneViewerArgs } from '@app/helpers/clara-viewer';
import { ConfiguratorHelper } from '@app/helpers/configurator.helper';
import { Dirs, eBundle } from '@app/helpers/dirs';
import { Kb3dViewer } from '@app/helpers/kb3d-viewer';
import { ISceneViewerArgs, SceneViewer } from '@app/helpers/scene-viewer';
import { ApiService } from '@app/services/api.service';
import { AuthService } from '@app/services/auth.service';
import { DialogService } from '@app/services/dialog.service';
import { DrawerService, IDrawer } from '@app/services/drawer.service';
import { KbService } from '@app/services/kb.service';
import { QuoteService } from '@app/services/quote.service';
import { RuleService } from '@app/services/rule.service';
import { SfdcService } from '@app/services/sfdc.service';
import { TelemetryService } from '@app/services/telemetry.service';
import { ThemeService } from '@app/services/theme.service';
import { UploadService } from '@app/services/upload.service';
import { BaseController } from '@app/views/base.view';
import {
    Build,
    CToken,
    Configurator,
    ConfiguratorAction,
    DialogButton,
    Expander,
    Field,
    Html,
    IAlert,
    IBuildRuleArgs,
    ICompany,
    IConfigurator,
    IConfiguratorLoadedRuleArgs,
    IConfiguratorMessageRuleArgs,
    IConfiguratorRuleArgs,
    IConfiguratorSession,
    IConfiguredProduct,
    IExpanderChangedRuleArgs,
    IFieldUploadOptions,
    IFixThisOption,
    IJsResult,
    IKb3dCallSceneFunctionArgs,
    IKbUser,
    IKeyboardArgs,
    IKeyboardRuleArgs,
    ILayout,
    ILoadedRuleArgs,
    INestedConfiguratorRuleArgs,
    IPriceColumn,
    IPriceItem,
    IPriceItemBase,
    IPriceObject,
    IPricingRuleArgs,
    IProduct,
    IProductTranslation,
    IQueue,
    IQuote,
    IQuoteProduct,
    IRootScope,
    IScene,
    ISceneTranslation,
    ISetSceneArgs,
    ISfdcCpqPayload,
    ITabChangedRuleArgs,
    ITable,
    ITableObject,
    ITestBuild,
    ITranslationPackage,
    IUploadValue,
    InputDialogButton,
    KbObject,
    KbObjectManager,
    LayoutConfig,
    NestedSet,
    Option,
    OptionFilter,
    Page,
    PropertyChangedEvent,
    RuleContainer,
    SvgViewer,
    TabControl,
    UiObject,
    Viewer,
    eAlertType,
    eColumnType,
    eEnvironment,
    eFormatType,
    eInputDialogType,
    eNestedSetDisplay,
    eRuleType,
    eValidationTiming,
    eViewType,
    eViewerMode,
    eWorkflowAction,
} from '@models';
import { ClientLogger, KPromise } from '@rules';
import { Color, IMessage, Utils, eScrollMode, icons } from '@tools';
import { StateParams, StateService } from '@uirouter/angularjs';
import * as angular from 'angular';
import { IScope } from 'angular';
import * as Q from 'q';
import QRCode from 'qrcode';
import { InteractionService } from '../../services/interaction.service';

export interface ITestBuildDialogScope extends ng.IScope {
    model?: {
        testBuildName: string;
        idQueue: number;
    };
    queues?: IQueue[];
    validation?: {};
}

export interface IGenerateQrCodeScope extends IScope {
    QrCode?: HTMLCanvasElement;
    autoStartAr?: boolean;
    updateQrCode?: any;
}

/**
 * Class to help with node & page binding.
 * The selected node is what kbTree binds to, and is also what configuratorController
 * sets when trying to set the active page through code or the rules. The selected page
 * is what drives which page is shown and is not necessarily the same thing.
 * There is a special case for nested configurators when they only have one visible page
 * (with no visible children of the page) that we try to show the nested configurator as
 * one node (instead of a node for the configurator, and another child node for the page).
 */
export class SelectedBinding {
    public pageChanged = new PropertyChangedEvent();

    /**
     * selected page is the page that is showing.  It is not always the selected node in
     * the tree though, as there are some special situations to consider (like showing a
     * nested config as one node when it only has one visible page).
     */
    private _page: any;
    public get page(): any {
        return this._page;
    }
    /**
     * private routine to set the page as consumers should all set the node
     * @param newPage
     */
    private setPage(newPage) {
        let oldPage = this._page;
        let changed = oldPage !== newPage;
        this._page = newPage;
        if (changed) this.pageChanged.trigger({ oldValue: oldPage, newValue: newPage, property: 'page' });
    }

    /**
     * node is bound to the kbTree to track the selected node.  All consumers should be setting this
     */
    private _node: Page | Configurator;
    public get node(): Page | Configurator {
        return this._node;
    }
    public set node(newNode) {
        if (newNode instanceof Configurator) {
            let firstPage = newNode.pages.find(p => p.visible);
            if (firstPage) {
                newNode = firstPage;
            }
        }
        let oldNode = this._node;
        let changed = oldNode !== newNode;
        this._node = newNode;

        if (changed) {
            let parentConfig = newNode instanceof Configurator ? newNode : newNode.$parentConfigurator;
            if (this.isSingleNode(parentConfig)) {
                this._node = parentConfig;
                this.setPage(newNode);
            } else {
                this._node = newNode;
                this.setPage(newNode);
            }
        }
    }

    /**
     * if this is the special case where a nested config is shown as a single node because it has only one visible page
     * @param tab
     */
    public isSingleNode(tab: Configurator) {
        return (
            tab.$parent &&
            !tab.pages.some(p => p.pages.some(p2 => p2.visible) || p.configurators.length > 0) &&
            tab.pages.filter(p => p.visible).length == 1
        );
    }
}

export interface ITabNode {
    id: string;
    level: number;
    node: Page | Configurator;
}

export interface IConfiguratorScope extends ng.IScope {
    config: Configurator;
    layoutConfig: LayoutConfig;
    parentQuote: IQuote;
    activeSceneViewer: SceneViewer;
    /** dummy term that is useful for re-using the same tab template for pages and configurators */
    tab: Configurator;
    priceObject: IPriceObject;
    pricePrecision: number;
    entity: IProduct;
    testBuild: (build: Build) => void;
    selected: SelectedBinding;
    runFixThis: (field: Field, fix: IFixThisOption) => void;
    runFieldAction: (field: Field) => void;
    runSvgAction: (svg: SvgViewer, $event: MouseEvent) => void;
    svgDragStart: (svg: SvgViewer, $event: MouseEvent) => void;
    svgDragEnd: (svg: SvgViewer, $event: MouseEvent) => void;
    svgDragMove: (svg: SvgViewer, $event: MouseEvent) => void;
    runHtmlAction: (html: Html, $event: MouseEvent) => void;
    runAction: (action: ConfiguratorAction) => ng.IPromise<any>;
    actionsVisible: boolean;
    fieldValueChange: (field: Field) => ng.IPromise<any>;
    fieldTouched: (field: Field) => void;
    tabChanged: (oldTab: string, newTab: string, tabControl: TabControl) => void;
    expanderChanged: (expander: Expander) => void;
    keydown: (event: KeyboardEvent) => void;
    nestedTabSelected: (oldTab, newTab: string, field: NestedSet) => void;
    nestedSetSelected: (field: NestedSet, nested: Configurator) => void;
    togglePages: (show: boolean) => void;
    /** whether the pages section is collapsed or not */
    showPages: boolean;
    pageClick: (e: ng.IAngularEvent, page: Page) => void;
    nextPage: () => void;
    selectNode: (node: Page | Configurator) => void;
    nextNode: Page | Configurator;
    backPage: () => void;
    backNode: Page | Configurator;
    isTest: boolean;
    /** the current viewer mode (depends on the settings of the currently selected page, nested scenes, etc) */
    viewerMode: string;
    /** the path to the media file showing in the viewer (depends on viewer settings in hierarchy of configurators) */
    viewerMediaPath: string;
    /** nodes calculate for page selection dropdown on mobile */
    nodes: ITabNode[];
    pageSlideVisible: boolean;
    togglePageSlide: (show: boolean) => void;
    /** returns if this is a quote product in a workflow, and the user doesn't have permission to modify the product */
    isReadOnly: boolean;
    isEmbed: boolean;
    resizeScene: () => void;
    translateKbo: (kbObject: KbObject, property: string) => string;
    layoutLoaded: boolean;

    /**
     * when a file input field or rules has requested a file upload.
     * The callback is called only when the imported file has been returned
     * to the client scene and is ready for use
     */
    upload: (file: File, field: Field) => ng.IPromise<any>;
    uploadCleared: (field: Field) => void;
    isScene: boolean;

    isNew: boolean;
    isEdit: boolean;
    isView: boolean;
    /** currency might change depending on whether we are in a quote or not */
    getCurrency: () => string;
    showPricing: (item?: IPriceItemBase) => void;
    priceColumns: IPriceColumn[];
    priceColumnDb: { [name: string]: IPriceColumn };

    //change display currency for sfdc
    sfdcCurrency: string;
    sfdcHasCurrency: boolean;

    isKinetic: boolean;

    /** show the moveme animation on loading the scene */
    showMove: boolean;
    embedCurrency: string;
    deferLoadedRule: boolean;
    embedFields: {};
    embedLayoutSettings: {};
    embedParameters: {};

    getSubmitButtonText: () => string;
    getSubmitAndStayButtonText: () => string;
    getGenerateQrCodeText: () => string;
    submitButtonClick: () => ng.IPromise<any>;

    arLoading: ng.IPromiseTracker;
    pricingTracker: ng.IPromiseTracker;

    showSpinnerPricing: boolean;
    /**
     * tracker that only tracks when the user has clicked the price button,
     * but we're waiting for the price before showing the dialog
     */
    priceDialogTracker: ng.IPromiseTracker;
    /** a dictionary of configurator id/names organized by the id of the pages they can be added to */
    userCanAddToDb: { [idProduct: number]: { [idPage: string]: { id: number; name: string }[] } };
    addNestedConfigClick: (e: ng.IAngularEvent, parent: Page | Configurator) => ng.IPromise<any>;
    addNestedConfig: (
        idNested: number,
        parent: Page | Configurator | NestedSet,
        addedByUser: boolean,
        name?: string
    ) => ng.IPromise<Configurator>;
    removeNestedConfig: (nested: Configurator) => void;
    copyNestedConfig: (sourceConfig: Configurator | Page) => ng.IPromise<any>;
    nestedClick: (e: ng.IAngularEvent, nested: Configurator) => void;

    render: () => void;
    /** autocomplete field controls asking for updated data */
    autoCompleteQuery: (query: string, field: Field) => ng.IPromise<Option[]>;
    /** needed for autocompletes that are using the label field to load the initial label to show */
    autoCompleteGetLabel: (value: any, field: Field) => ng.IPromise<string>;

    hasChildren: (tab: Page | Configurator) => boolean;

    /** needed for svg filters.  See here: http://stackoverflow.com/questions/19742805/angular-and-svg-filters */
    absUrl: string;
}

export interface IPriceObjectDialogScope extends ng.IScope {
    /** named so it can have a consistent name for hierarchy in the view */
    item?: IPriceObject;
    getCurrency?: () => string;
    title?: string;
    openItem?: (item) => void;
    smartColorStyle?: (level: number) => string;
    flat?: boolean;
    getColumnValue?: (col: IPriceColumn, item: IPriceItemBase) => string;
    infiniteScroll?: () => void;
    itemsInView?: IPriceItemBase[];
}

export class ConfiguratorRuleArgs implements IConfiguratorRuleArgs, INestedConfiguratorRuleArgs {
    constructor(private ctrl: ConfiguratorController) {
        this.isMobile = ctrl.$rootScope.windowWidth < Utils.MOBILE_WIDTH;
        this.windowWidth = ctrl.$rootScope.windowWidth;
        this.windowHeight = ctrl.$rootScope.windowHeight;
    }

    public company: ICompany;
    public configurator: Configurator;
    public layoutConfig: LayoutConfig;
    public user: IKbUser;
    public kom: KbObjectManager;
    public parentKom: KbObjectManager;
    public changedField: Field;
    public selectedPage: Page;
    public nested: Configurator;
    public environment: string;
    public baseUrl: string;
    public parameters: any;
    public clientLanguage: string;
    public logs: ClientLogger;
    public isMobile: boolean;
    public windowWidth: number;
    public windowHeight: number;
    public viewerMode: string;
    public isCrm: boolean;
    private activeUrl: string = window.location.href;

    public getTables(args): KPromise<ITableObject[]> {
        return new KPromise(args.ids.map(tid => this.ctrl.helper.tableArraysDb[tid]));
    }
    public upload() {
        return this.ctrl.uploadService.promptUserToChooseFile().then(file => {
            return this.ctrl.$scope.upload(file, null);
        });
    }
    public sendMessage(msg: IMessage) {
        // send message to embed consumer
        Utils.sendMessageToParent(msg);

        this.ctrl.sfdcService.sendMessage(msg);
    }
    public callSafeFunction(obj) {
        return this.ctrl.api.safeFunctions.run(obj.safeFunctionId, obj.parameters, this.ctrl.$rootScope.contentTracker);
    }
    public startIntervalFunction(id, timeSeconds) {
        return this.ctrl.runContinuousAsyncRule(id, timeSeconds);
    }
    public stopIntervalFunction(id) {
        this.ctrl.cancelScheduledRule(id);
    }
    public startTimeoutFunction(func, timeSeconds) {
        this.ctrl.runTimeoutRule(func, timeSeconds);
    }
    public convertCurrency(obj) {
        return this.ctrl.kbService.fxConvertAsync(obj.amount, obj.currencyCode);
    }
    public generateNumber(args) {
        return this.ctrl.api.generators.next(args.generatorId, this.ctrl.$rootScope.contentTracker);
    }
    public selectPage(page) {
        this.ctrl.$timeout(() => this.ctrl.selectNode(page));
    }
    public navigateToElement(elem) {
        this.ctrl.navigateToElement(elem);
    }
    public nextPage() {
        this.ctrl.$timeout(() => this.ctrl.$scope.nextPage());
    }
    public backPage() {
        this.ctrl.$timeout(() => this.ctrl.$scope.backPage());
    }
    public runSceneAction(name) {
        return this.ctrl.runSceneAction(this.configurator, name);
    }
    public callSceneFunction(args: IKb3dCallSceneFunctionArgs) {
        let sv = this.ctrl.$scope.activeSceneViewer;
        if (sv && sv instanceof Kb3dViewer) {
            return sv.runSceneFunction(args, this.configurator).then(r => r.parameters.returnValue);
        }

        console.warn(`Scene function ${args.name} was called but a scene is not loaded.`);
        return Promise.resolve(null);
    }
    public setScene(args: ISetSceneArgs): ng.IPromise<any> {
        return this.ctrl.setScene(this.configurator, args.idScene);
    }
    public runNamingRule() {
        return this.ctrl.helper.runRuleTypeAsync(this.configurator, eRuleType.naming);
    }
    public addNestedConfigurator(idProduct, idParent, name?: string, fields?: {}) {
        // this call already came from a rule, so we don't need to run the oncePerRuleCycle here
        return this.ctrl.addNestedConfig(
            idProduct,
            this.configurator.$manager.get(idParent) as Page | Configurator | NestedSet,
            false,
            name,
            fields
        );
    }
    public copyNestedConfigurator(nested: Configurator | string, idParent, name?: string) {
        let sourceConfig: Configurator = nested as Configurator;
        if (angular.isString(nested)) {
            sourceConfig = this.configurator.$manager.getByName<Configurator>(nested as string, false);
        }
        return this.ctrl.copyNestedConfig(
            sourceConfig,
            this.configurator.$manager.get(idParent) as Page | Configurator | NestedSet,
            false,
            name
        );
    }
    public setNumberOfNestedConfigurators(idProduct, idParent, n) {
        return this.ctrl.setNumberOfNestedConfigs(
            idProduct,
            this.configurator.$manager.get(idParent) as Page | Configurator | NestedSet,
            n
        );
    }
    public removeNestedConfigurator(nested: Configurator | string) {
        let nestedConfig: Configurator = nested as Configurator;
        if (angular.isString(nested)) {
            nestedConfig = this.configurator.$manager.getByName<Configurator>(nested as string, false);
        }
        return this.ctrl.removeNestedConfig(nestedConfig);
    }
    public showPriceDetails() {
        this.ctrl.$scope.showPricing();
    }
    public alert(args: IAlert) {
        this.ctrl.dialogService.alert(args);
    }
    public getOptionFilterSource(idOptionFilter: string, fromConfigurator?: Configurator) {
        fromConfigurator = fromConfigurator || this.configurator;
        let f = fromConfigurator.$manager.get(idOptionFilter) as OptionFilter;
        return new KPromise(f ? f.$source : []);
    }
    public setLayoutSetting(id: string, value: any) {
        if (!this.configurator.isNested()) {
            //nested configurators should not be able to override the root's layout settings
            let s = this.layoutConfig.$manager.getByIdOrName<Field>(id, true);
            let origValue = s.value;
            s.value = value;
            if (origValue != s.value) {
                this.layoutConfig.$layoutSettingsDirty = true;
            }
        }
    }
    public getLayoutSetting(id: string) {
        let s = this.layoutConfig.$manager.getByIdOrName<Field>(id, true);
        return s.value;
    }
    public toggleFullscreen() {
        this.ctrl.$rootScope.toggleFullscreen();
    }
    public runConfiguredProduct(configProd: IConfiguredProduct) {
        //run the configured product after this rule has ended
        this.ctrl.runConfiguredProduct(this.configurator, configProd);
    }
}

export class SvgRuleArgs extends ConfiguratorRuleArgs {
    public elementId: string;
    public mouseX: number;
    public mouseY: number;
    public commitDrag: boolean;
    public originMouseX: number;
    public originMouseY: number;
    public originElementId: string;
}

export class HtmlRuleArgs extends ConfiguratorRuleArgs {
    public elementId: string;
    public mouseX: number;
    public mouseY: number;
}

@NgView({
    token: Token.ConfiguratorView,
    dependencies: [
        Token.$Scope,
        Token.$Q,
        Token.$Location,
        Token.$State,
        Token.$StateParams,
        Token.$Http,
        Token.$RootScope,
        Token.$Window,
        Token.$Compile,
        Token.$Injector,
        Token.DialogService,
        Token.$Timeout,
        Token.$Interval,
        Token.$Filter,
        Token.DrawerService,
        Token.RuleService,
        Token.UploadService,
        Token.AuthService,
        Token.QuoteService,
        Token.PromiseTracker,
        Token.ApiService,
        Token.ThemeService,
        Token.SfdcService,
        Token.KbService,
        Token.TelemetryService,
        Token.InteractionService,
        Token.$OcLazyLoad,
    ],
})
export class ConfiguratorController extends BaseController {
    constructor(
        public $scope: IConfiguratorScope,
        public $q: ng.IQService,
        public $location: ng.ILocationService,
        public $state: StateService,
        public $stateParams: StateParams,
        public $http: ng.IHttpService,
        public $rootScope: IRootScope,
        public $window: ng.IWindowService,
        public $compile: ng.ICompileService,
        public $injector: ng.auto.IInjectorService,
        public dialogService: DialogService,
        public $timeout: ng.ITimeoutService,
        public $interval: ng.IIntervalService,
        public $filter: ng.IFilterService,
        public drawerService: DrawerService,
        public ruleService: RuleService,
        public uploadService: UploadService,
        public authService: AuthService,
        public quoteService: QuoteService,
        public promiseTracker: ng.IPromiseTracker,
        public api: ApiService,
        public themeService: ThemeService,
        public sfdcService: SfdcService,
        public kbService: KbService,
        public telemetryService: TelemetryService,
        public interactionService: InteractionService,
        public ocLazyLoad: oc.ILazyLoad
    ) {
        super();

        this.model = {};
        this.configuratorLoadedDeferred = this.$q.defer();
        this.$scope.layoutLoaded = false;
        this.$rootScope.contentTracker.addPromise(this.configuratorLoadedDeferred.promise);
        let isScene = ($scope.isScene = $state.current.name == 'scene');
        let queryString = $location.search();

        $scope.isTest = false;
        if ($state.current.data) {
            $scope.isEdit = $state.current.data.viewType == eViewType.edit;
            $scope.isNew = $state.current.data.viewType == eViewType.new;
            $scope.isView = $state.current.data.viewType == eViewType.view;
            $scope.isTest = $state.current.data.isTest == true;
        }
        if ($scope.isTest) {
            this.drawerService.setHelpUrl('Configurators');
        } else {
            this.drawerService.setHelpUrl(null);
        }

        // setup binding to handle selected page and node
        $scope.selected = new SelectedBinding();

        $scope.pricingTracker = promiseTracker();
        $scope.priceDialogTracker = promiseTracker();
        $scope.arLoading = promiseTracker();
        $scope.showSpinnerPricing = true;

        // $scope.showConfigHeader = (queryString.showconfigheader != "false");
        // $scope.showFields = (queryString.showfields != "false");
        $scope.showMove = queryString.showmove == 'true';
        $scope.isEmbed = Utils.isDefined(queryString.embedded);
        $scope.embedFields = queryString['fields'] ? JSON.parse(queryString['fields']) : null;
        $scope.embedLayoutSettings = queryString['layoutsettings'] ? JSON.parse(queryString['layoutsettings']) : null;
        $scope.embedParameters = queryString['parameters'] ? JSON.parse(queryString['parameters']) : null;
        $scope.embedCurrency = queryString['currency'];
        $scope.deferLoadedRule = queryString['deferLoadedRule'] == 'true';
        $scope.isKinetic = !!queryString['kineticCompany'];

        // only allow the embed currency if it's a defined company currency
        if (!this.$rootScope.companyCurrencies.some(c => c.currency == $scope.embedCurrency)) {
            $scope.embedCurrency = null;
        }
        $scope.actionsVisible = true;
        $scope.absUrl = $location.absUrl();
        $scope.isReadOnly = false;

        if ($scope.isEmbed) {
            $rootScope.hideDrawer = queryString.showdrawer != 'true';
            $rootScope.transparentBackground = queryString.transparentbackground == 'true';
        }

        if (Utils.isDefined(queryString.showheader) && queryString.showheader == 'false') {
            $rootScope.hideHeader = true;
        }

        this.requestId = queryString.requestid;

        this.helper = new ConfiguratorHelper({
            $http: this.$http,
            $q: this.$q,
            api: this.api,
            dialogService: this.dialogService,
            ruleService: this.ruleService,
            tracker: this.$rootScope.contentTracker,
            $rootScope: this.$rootScope,
            getRuleArgs: (c, f) => this.getRuleArgs(c, f),
            showErrors: () => this.showErrors(),
        });

        $scope.translateKbo = (o, prop) => {
            if (o) {
                if (this.$rootScope.companySettings.defaultLanguage != this.$rootScope.clientLanguage) {
                    var parentConfig = o instanceof Configurator ? o : o.$parentConfigurator;
                    var configDb = this.translationDb[parentConfig.idProduct || parentConfig.idScene];
                    if (configDb && configDb.hasOwnProperty(o.id) && configDb[o.id].hasOwnProperty(prop)) {
                        return configDb[o.id][prop];
                    }
                }
                var realProp = prop == 'name' ? '$label' : prop;
                return o[realProp];
            }
            return '';
        };

        this.getConfigurator().then(rootConfig => {
            if (rootConfig == null) return;
            this.rootConfig = $scope.config = $scope.tab = rootConfig;

            this.interactionService.record(this.rootConfig.idProduct);

            $scope.selected.pageChanged.add(args => {
                // run the pageChanged rule
                let pageParentConfig = args.newValue.$parentConfigurator;
                let pageChangedArgs = this.getRuleArgs(pageParentConfig);
                pageChangedArgs.selectedPage = args.newValue;
                if (window.location.href.includes('ar=true')) {
                    this.enterArModal();
                }
                this.trackConfigPageView(pageChangedArgs.selectedPage);
                this.queueRule(() => {
                    return this.helper
                        .runRuleTypeAsync(pageParentConfig, eRuleType.pageChanged, pageChangedArgs)
                        .then(r => {
                            // make sure all nodes are expanded
                            if (args.newValue) {
                                let parent: KbObject = args.newValue;
                                while (parent) {
                                    parent.$expanded = true;
                                    parent = parent.$parent;
                                }
                            }
                            if (!this.loading && pageParentConfig.getRuleJs(eRuleType.pageChanged)) {
                                return this.runRuleCycleUnit(pageParentConfig, null).then(() =>
                                    this.oncePerCycleRule()
                                );
                            } else {
                                // refresh viewer so we can change scenes for
                                // nested configurators with scenes that aren't nesting
                                this.getCurrentConfigForViewerAndSetViewerMode(); //set the viewermode before layout rule so it can show/hide the viewer accordingly
                                return this.runLayoutRule(
                                    this.$scope.config,
                                    this.getRuleArgs(this.$scope.config)
                                ).then(() => {
                                    return this.refreshViewer(true).then(() => {
                                        // even if not running the value rule, we still need to
                                        // update the nodes for the next / back buttons
                                        this.calculateNodes();
                                    });
                                });
                            }
                        });
                });
            });

            $scope.testBuild = (build: Build) => {
                this.runSubmitRule().then(() => {
                    this.helper.runRuleTypeAsync(this.rootConfig, eRuleType.naming);
                    let buildRuleArgs: IBuildRuleArgs = this.getRuleArgs(this.rootConfig) as any;
                    buildRuleArgs.build = build;
                    buildRuleArgs.quote = {
                        name: 'Test Quote',
                        id: 1,
                        ownedBy: this.$rootScope.user.id,
                        owner: this.$rootScope.user.firstName + ' ' + this.$rootScope.user.lastName,
                        description: '',
                        currency: 'USD',
                    };
                    buildRuleArgs.quoteProduct = {
                        isConfigured: true,
                        id: 1,
                        qty: 1,
                        priceObject: this.$scope.priceObject,
                        name: this.rootConfig.name,
                        description: this.rootConfig.description,
                        shortDescription: this.rootConfig.shortDescription,
                    };

                    let displayDialog = (queueId: number) => {
                        let dialogScope: ITestBuildDialogScope = $rootScope.$new();
                        dialogScope.model = {
                            testBuildName: this.rootConfig.name + ' ' + build.name + ' Test Build',
                            idQueue: queueId,
                        };
                        dialogScope.validation = {};
                        api.queues.search().then(r => (dialogScope.queues = r));

                        dialogService.dialog({
                            template: Dirs.view('dialog-test-build'),
                            scope: dialogScope,
                            buttons: [
                                new InputDialogButton(loc.ok, icons.success, args => {
                                    if (!dialogScope.model.testBuildName) {
                                        dialogScope.validation['testBuildName'] = 'Name is required';
                                        args.close = false;
                                        return;
                                    }
                                    if (dialogScope.model.testBuildName.length > 400) {
                                        dialogScope.validation['testBuildName'] =
                                            'Name must be less than 400 characters';
                                        args.close = false;
                                        return;
                                    }
                                    this.api.testBuilds
                                        .insert(
                                            {
                                                idBuildType: build.idBuildType,
                                                idProduct: parseInt($state.params.id, 10),
                                                name: dialogScope.model.testBuildName,
                                                idQueue: dialogScope.model.idQueue,
                                                configuredProduct: this.rootConfig.getConfiguredProduct(),
                                                cloudBuild: build.cloudBuild,
                                                priceObject: this.$scope.priceObject,
                                            } as ITestBuild,
                                            this.$rootScope.contentTracker
                                        )
                                        .then(testBuild => {
                                            dialogService.confirm(
                                                'Your configurator is building ' +
                                                    build.name +
                                                    ' outputs. Would you like to go to the test build page?',
                                                () => {
                                                    $state.go('kb.admin.testBuild', { id: testBuild.id });
                                                }
                                            );
                                        });
                                }),
                                new InputDialogButton(loc.cancel, icons.cancel, args => {}),
                            ],
                        });
                    };

                    try {
                        this.helper
                            .runRuleContainerAsync(
                                build.ruleContainers.find(rc => rc.ruleType == eRuleType.beforeBuild),
                                buildRuleArgs
                            )
                            .then(
                                (result: IJsResult<IBuildRuleArgs>) => {
                                    let queueId = result.parameters.build.idQueue;
                                    displayDialog(queueId);
                                },
                                () => {
                                    // We'll want to display the dialog even if this rule fails,
                                    // since it's possible it's due to functionality that's only available in server-side rules.
                                    displayDialog(build.idQueue);
                                }
                            );
                    } catch (e) {
                        // We'll want to display the dialog even if this rule fails, since it's possible it's due to
                        // functionality that's only available in server-side rules.
                        displayDialog(build.idQueue);
                    }
                });
            };

            $scope.getSubmitButtonText = () => {
                return this.$scope.config.submitButtonText ? this.$scope.config.submitButtonText : loc.addtoquote;
            };

            $scope.getSubmitAndStayButtonText = () => {
                return this.$scope.config.submitAndStayButtonText
                    ? this.$scope.config.submitAndStayButtonText
                    : loc.saveandstay;
            };

            $scope.getGenerateQrCodeText = () => {
                return this.$scope.config.generateQrCodeText
                    ? this.$scope.config.generateQrCodeText
                    : loc.generateqrcode;
            };

            $scope.submitButtonClick = () => {
                if ($scope.isEmbed) {
                    Utils.sendMessageToParent({ name: 'submit', data: {} });
                } else {
                    return this.submit();
                }
            };

            $scope.runFixThis = (field: Field, fix: IFixThisOption) => {
                return this.queueRule(() => {
                    return (new KPromise(fix.fix()) as any as ng.IPromise<any>)
                        .then(() => this.runRuleCycleUnit(field.$parentConfigurator, null))
                        .then(() => this.oncePerCycleRule());
                });
            };

            let getTargetElementId = ($event: MouseEvent) => {
                let el = <any>$event.target;
                let elementId = el.id;
                while (!elementId && el) {
                    if (el.tagName == 'svg') break;
                    elementId = el.id;
                    el = el.parentElement;
                }
                return elementId;
            };

            let getSvgArgs = (svgViewer, $event: MouseEvent, $originalStartEvent: MouseEvent = null): SvgRuleArgs => {
                let c = svgViewer.$parentConfigurator;
                let args: any = this.getRuleArgs(c) as SvgRuleArgs;
                args.elementId = getTargetElementId($event);
                args.mouseX = $event.offsetX;
                args.mouseY = $event.offsetY;
                args.commitDrag = false;

                if ($originalStartEvent) {
                    args.originMouseX = $originalStartEvent.offsetX;
                    args.originMouseY = $originalStartEvent.offsetY;
                    args.originElementId = getTargetElementId($originalStartEvent);
                }
                return args;
            };

            $scope.runSvgAction = (svgViewer, $event: MouseEvent) => {
                let rc = svgViewer.ruleContainers.find(rc => rc.ruleType == eRuleType.svgViewer);
                if (rc) {
                    let args = getSvgArgs(svgViewer, $event);
                    return this.queueEventRule(svgViewer.$parentConfigurator, rc, args);
                }
            };

            let originalDragStartEvent: MouseEvent = null;
            let svgViewerShouldEnablePanAfterDrag = false;
            $scope.svgDragStart = (svgViewer, $event: MouseEvent) => {
                let rc = svgViewer.ruleContainers.find(r => r.ruleType == eRuleType.dragStart);
                if (rc) {
                    let args = getSvgArgs(svgViewer, $event, originalDragStartEvent);
                    return this.helper.runRuleContainerAsync<SvgRuleArgs>(rc, args).then(result => {
                        if (!result.hasError && result.parameters.commitDrag) {
                            originalDragStartEvent = $event;
                            svgViewerShouldEnablePanAfterDrag = !svgViewer.disablePan;
                            svgViewer.disablePan = true;
                        }
                    });
                }
            };

            $scope.svgDragMove = (svgViewer, $event: MouseEvent) => {
                if (originalDragStartEvent) {
                    let rc = svgViewer.ruleContainers.find(r => r.ruleType == eRuleType.dragMove);
                    if (rc) {
                        let args = getSvgArgs(svgViewer, $event, originalDragStartEvent);
                        return this.helper.runRuleContainerAsync<any>(rc, args).then(() => $scope.$digest());
                    }
                }
            };

            $scope.svgDragEnd = (svgViewer, $event: MouseEvent) => {
                if (originalDragStartEvent) {
                    let rc = svgViewer.ruleContainers.find(r => r.ruleType == eRuleType.dragEnd);
                    if (rc) {
                        let args = getSvgArgs(svgViewer, $event, originalDragStartEvent);
                        originalDragStartEvent = null;
                        svgViewer.disablePan = !svgViewerShouldEnablePanAfterDrag;
                        return this.queueEventRule(svgViewer.$parentConfigurator, rc, args);
                    }
                }
            };

            let getHtmlArgs = (htmlViewer, $event: MouseEvent, $originalStartEvent: MouseEvent = null): SvgRuleArgs => {
                let c = htmlViewer.$parentConfigurator;
                let args: any = this.getRuleArgs(c) as HtmlRuleArgs;
                args.elementId = getTargetElementId($event);
                args.mouseX = $event.offsetX;
                args.mouseY = $event.offsetY;
                
                return args;
            };

            $scope.runHtmlAction = (html, $event: MouseEvent) => {

                let rc = html.ruleContainers.find(rc => rc.ruleType == eRuleType.html);
                if (rc) {
                    let args = getHtmlArgs(html, $event);
                    return this.queueEventRule(html.$parentConfigurator, rc, args);
                }
            };

            $scope.runFieldAction = field => {
                if (isScene) {
                    return this.$scope.activeSceneViewer && this.$scope.activeSceneViewer.runFieldAction(field);
                } else {
                    return this.queueEventRule(
                        field.$parentConfigurator,
                        field,
                        this.getRuleArgs(field.$parentConfigurator)
                    );
                }
            };

            $scope.runAction = action => {
                if (isScene) {
                    return this.queueRule(() => this.runSceneAction(this.rootConfig, action.name));
                } else {
                    return this.queueEventRule(
                        action.$parentConfigurator,
                        action,
                        this.getRuleArgs(action.$parentConfigurator)
                    );
                }
            };

            $scope.fieldValueChange = field => {
                if (!this.loading) {
                    return this.queueRule(() => {
                        return this.helper
                            .runRuleContainerAsync(field)
                            .then(() => this.runRuleCycleUnit(field.$parentConfigurator, field))
                            .then(() => this.oncePerCycleRule());
                    });
                }
            };

            $scope.fieldTouched = field => {
                field.hasBeenTouched = true;
                if (field.$parentConfigurator.validationTiming == eValidationTiming.onTouched) {
                    this.rootConfig.isValid(); //recalculate root as the field might be in a nested set, which is in a page, etc.
                }
            };

            $scope.tabChanged = (oldTab, newTab, tabControl) => {
                if (!this.loading) {
                    let c = tabControl.$parentConfigurator;
                    let args: ITabChangedRuleArgs = this.getRuleArgs(c) as any;
                    args.oldTab = c.$manager.getByName(oldTab, false);
                    args.newTab = c.$manager.getByName(newTab, false);
                    args.tabControl = tabControl;
                    let origNumberOfNesteds = c.getNestedConfigurators().length;

                    return this.queueEventRuleType(c, eRuleType.tabChanged, args);
                }
            };

            $scope.keydown = (k: KeyboardEvent) => {
                if (
                    !(
                        k.target instanceof HTMLInputElement ||
                        k.target instanceof HTMLTextAreaElement ||
                        (k.target instanceof HTMLElement && k.target.classList.contains('kb-no-keyboard-rule'))
                    )
                ) {
                    let keyArgs = {
                        event: k,
                        key: k.key,
                        keyCode: k.keyCode,
                        altKey: k.altKey,
                        ctrlKey: k.ctrlKey || k.metaKey,
                        shiftKey: k.shiftKey,
                        repeat: k.repeat,
                    } as IKeyboardArgs;

                    let ruleArgs: IKeyboardRuleArgs = this.getRuleArgs($scope.config) as any;
                    ruleArgs.keyArgs = keyArgs;

                    return this.queueEventRuleType($scope.config, eRuleType.keyboard, ruleArgs);
                }
            };

            $scope.expanderChanged = expander => {
                if (!this.loading) {
                    let c = expander.$parentConfigurator;
                    let args: IExpanderChangedRuleArgs = this.getRuleArgs(c) as any;
                    args.expander = expander;
                    let origNumberOfNesteds = c.getNestedConfigurators().length;

                    return this.queueEventRuleType(c, eRuleType.expanderChanged, args);
                }
            };

            $scope.nestedTabSelected = (oldTab, nestedName, nestedSet: NestedSet) => {
                let newTab = nestedSet.$parentConfigurator.$manager.getByName<Configurator>(nestedName, false);
                return $scope.nestedSetSelected(nestedSet, newTab);
            };

            $scope.nestedSetSelected = (nestedSet: NestedSet, nested: Configurator) => {
                if (!this.loading) {
                    let c = nested;
                    let args: INestedConfiguratorRuleArgs = this.getRuleArgs(nestedSet.$parentConfigurator) as any;
                    args.nested = c;
                    let parentConfig = c.$parentConfigurator;
                    return this.queueEventRule(nestedSet.$parentConfigurator, nestedSet, args);
                }
            };

            $scope.togglePages = show => {
                let changed = $scope.showPages != show;
                $scope.showPages = show;
                if (changed) {
                    $scope.resizeScene();
                    this.refreshDrawer();
                }
            };

            $scope.resizeScene = () => {
                if ($scope.activeSceneViewer) {
                    $timeout(() => {
                        $timeout(() => {
                            $scope.activeSceneViewer.resize();
                            $scope.activeSceneViewer.refreshElementParent();
                        });
                    }, 0);
                }
            };

            $scope.pageClick = (e, page: Page) => {
                e.stopPropagation();
                $scope.togglePages(true);
            };

            $scope.nextPage = () => {
                if (this.$scope.nextNode) this.selectNode(this.$scope.nextNode);
            };

            $scope.backPage = () => {
                if (this.$scope.backNode) this.selectNode(this.$scope.backNode);
            };

            $scope.nestedClick = (e, nested) => {
                e.stopPropagation();
                this.selectNode(nested);
                // var firstPage = nested.pages.find(p => p.visible);
                // if (firstPage) {
                //    this.selectNode(firstPage);
                // }
            };

            $scope.hasChildren = tab => {
                if (tab instanceof Configurator && $scope.selected.isSingleNode(tab)) {
                    return tab.configurators.length > 0;
                } else {
                    return tab.pages.some(p => p.visible) || tab.configurators.length > 0;
                }
            };

            let thisCtrl = this;

            $scope.upload = function (file, field: Field) {
                let uploadOptions = {
                    convertPdfToImage: field.convertUploadPdfToImage,
                } as IFieldUploadOptions;
                return thisCtrl.uploadService.uploadConfiguredProductFile(file, uploadOptions).then(uuid => {
                    let uploadValue = field.value as IUploadValue;
                    uploadValue.path = uuid;
                    field.$parentConfigurator.$fieldsDirty = true;
                    field.$parentConfigurator.$fieldsDirtyForSceneRules = true;
                    // clear the asset id in case there was an old file there that was just
                    // replaced to make sure the new file is uploaded to the scene
                    uploadValue.assetId = null;
                    if (thisCtrl.$scope.activeSceneViewer && thisCtrl.$scope.activeSceneViewer.scene.external) {
                        let sceneUploadPromise = thisCtrl.$scope.activeSceneViewer.importUploadFieldImage(
                            field.$parentConfigurator,
                            field,
                            file
                        );
                        thisCtrl.$rootScope.contentTracker.addPromise(sceneUploadPromise);
                        return sceneUploadPromise;
                    }
                });
            };

            $scope.uploadCleared = (field: Field) => {
                field.$parentConfigurator.$fieldsDirty = true;
                field.$parentConfigurator.$fieldsDirtyForSceneRules = true;
            };

            $scope.getCurrency = () => {
                // if there is a quote, then that overrides everything
                if ($scope.parentQuote) {
                    return $scope.parentQuote.currency;
                } else if (quoteService.quote) {
                    return quoteService.quote.currency;
                } else {
                    //if this is an sfdc cpq, use the incoming currency if provided
                    if (this.$scope.sfdcHasCurrency) {
                        //console.log(this.$rootScope.context.sfdcConvertCurrency ?? 'convert currency not set');
                        return this.$scope.sfdcCurrency;
                    }
                    // if we are in an embed, and the embed currency was specified, then use it
                    if (this.$scope.isEmbed && this.$scope.embedCurrency) {
                        return this.$scope.embedCurrency;
                    } else {
                        // just use the default company currency
                        return this.$rootScope.companySettings.currency;
                    }
                }
            };

            $scope.showPricing = (item?: IPriceItemBase) => {
                if (this.lastPricingPromise) {
                    this.$scope.priceDialogTracker.addPromise(this.lastPricingPromise);
                    this.lastPricingPromise.then(() => {
                        let dialogScope: IPriceObjectDialogScope = $scope.$new();
                        dialogScope.itemsInView = [];
                        dialogScope.item = item || $scope.priceObject;
                        this.addLevelsToPricing(dialogScope.item);
                        dialogScope.flat =
                            dialogScope.item.items && !dialogScope.item.items.some(i => i.items.length > 0);
                        dialogScope.getCurrency = $scope.getCurrency;
                        dialogScope.title = (item ? (item as IPriceItem).sku : this.rootConfig.name) + ' Pricing';
                        dialogScope.getColumnValue = (col, item) => {
                            let val = col.systemColumn
                                ? item[col.name.toCamelCase()]
                                : item.columns[col.name.toCamelCase()];

                            let format = col.format;
                            if (col.name.isEqual('discount')) {
                                if ((item.useRollup && item.items && item.items.length) || item.discountType == '$') {
                                    val = item.totalDiscount;
                                    format = eFormatType.currency;
                                } else {
                                    format = eFormatType.percentage;
                                }
                            }
                            if (col.type == eColumnType.number) {
                                const minPrecision = Number(col.formatMinPrecision);
                                const precision = Number(col.precision);
                                if (format == eFormatType.currency && precision === 2 && minPrecision === 2) {
                                    val = $filter<any>('fxConvert')(val, $scope.getCurrency());
                                } else {
                                    if (format == eFormatType.currency) {
                                        val = kbService.fxConvert(val, $scope.getCurrency());
                                    }

                                    val = Number(val).format({
                                        formatType: format,
                                        minPrecision: minPrecision,
                                        maxPrecision: precision,
                                        currency: $scope.getCurrency(),
                                        decimalSeparator: col.formatDecimalSeparator,
                                        thousandsSeparator: col.formatThousandsSeparator,
                                        prefix: col.formatPrefix,
                                        suffix: col.formatSuffix,
                                    });
                                }
                            }
                            return val;
                        };
                        dialogScope.openItem = (nestedItem: IPriceItem) => {
                            // $scope.showPricing(nestedItem)
                            (nestedItem as any).$expanded = !(nestedItem as any).$expanded;
                        };
                        dialogScope.smartColorStyle = level => {
                            let background = new Color(this.themeService.getActiveTheme().background);
                            if (level) {
                                if (background.isDark()) {
                                    background = Color.lighten(background.getOriginalInput(), level * 6);
                                } else {
                                    background = Color.darken(background.getOriginalInput(), level * 6);
                                }
                            }
                            let color = background.isDark() ? 'white' : 'black';
                            return 'background: ' + background.toRgbString() + '; color: ' + color + ';';
                        };
                        dialogScope.infiniteScroll = () => {
                            let pageSize = 50;
                            let totalCount = $scope.priceObject.items.length;
                            let currCount = dialogScope.itemsInView.length;
                            if (totalCount > currCount) {
                                let i = currCount;
                                if (i < 0) i = 0;
                                dialogScope.itemsInView.pushArray($scope.priceObject.items.slice(i, i + 50));
                            }
                        };
                        dialogScope.infiniteScroll();
                        dialogService.dialog({
                            fullHeight: true,
                            template: Dirs.view('dialog-price-object'),
                            scope: dialogScope,
                            buttons: [
                                new DialogButton(loc.close, icons.cancel, args => {
                                    if ($scope.isEmbed) {
                                        Utils.sendMessageToParent({ name: 'pricingDetailsClosed', data: {} });
                                    }
                                }),
                            ],
                        });

                        if ($scope.isEmbed) {
                            Utils.sendMessageToParent({ name: 'pricingDetailsShown', data: {} });
                        }
                    });
                }
            };

            $scope.addNestedConfigClick = (e, parent) => {
                // if the parent only has one type of referenced configurator, then we add it
                // if more than 1 ref config, then let control pass to the dropdown
                let parentConfig =
                    parent instanceof Configurator ? (parent as Configurator) : parent.$parentConfigurator;
                let entries = $scope.userCanAddToDb[parentConfig.idProduct][parent.id];
                if (entries && entries.length == 1) {
                    e.stopPropagation();
                    return $scope.addNestedConfig(entries[0].id, parent, true);
                }
                return new KPromise(null) as any;
            };

            $scope.addNestedConfig = (idNested, parent, addedByUser, name) => {
                return this.addNestedConfig(idNested, parent, addedByUser, name).then((config: Configurator) => {
                    return this.runRuleCycleUnit(config.$parentConfigurator, null).then(() =>
                        this.oncePerCycleRule().then(() => config)
                    );
                });
            };

            $scope.copyNestedConfig = source => {
                let sourceConfig =
                    source instanceof Configurator ? (source as Configurator) : source.$parentConfigurator;

                return this.copyNestedConfig(sourceConfig, sourceConfig.$parent as any, true).then(target =>
                    this.nestedRunBottomsUp(target, c => this.runRuleCycleUnit(c, null))
                );
            };

            $scope.removeNestedConfig = nested => {
                // might be a page or configurator passed in.  If it's a page,
                // it's the page's parent configurator that needs to be deleted
                if (nested instanceof Page || nested instanceof NestedSet)
                    nested = (nested as Page).$parentConfigurator as Configurator;

                this.dialogService.confirm("Are you sure you want to remove the item '" + nested.name + "'?", () =>
                    this.removeNestedConfig(nested).then(() => {
                        let parentConfig =
                            nested.$parent instanceof Configurator
                                ? (nested.$parent as Configurator)
                                : nested.$parent.$parentConfigurator;
                        return this.runRuleCycleUnit(parentConfig, null).then(() => this.oncePerCycleRule());
                    })
                );
            };

            $scope.autoCompleteQuery = (query, field) => {
                return this.helper.autoCompleteQuery(query, field);
            };
            $scope.autoCompleteGetLabel = (value, field) => {
                return this.helper.autoCompleteGetLabel(value, field);
            };

            $scope.togglePageSlide = show => {
                $scope.pageSlideVisible = show;
            };

            $scope.selectNode = node => {
                this.selectNode(node);
            };

            // $scope.render = () => {
            //    if (this.$scope.sceneViewer) {

            //        var promise = this.$scope.sceneViewer.render().then((url) => {
            //            this.rootConfig.viewerMediaPath = url;
            //        });

            //        this.dialogService && this.dialogService.alert({
            //            msg: "Your rendering is being processed",
            //            persist: false,
            //            timeShown: 1,
            //            promise: promise
            //        });

            //        return promise;
            //    }

            //    return $q.when(null);
            // };

            // when used in an iframe, we need to catch messages posted from the outside world to respond
            let messageHandler = event => {
                let msg: IMessage = event.data;
                if (msg) {
                    let data = msg.data;
                    if (msg.name) {
                        let messageName = msg.name.toLowerCase();
                        if (messageName == 'setfields') {
                            $scope.$apply(() => {
                                this.queueRule(() => {
                                    $scope.config.setFields(data);
                                    return this.runRuleCycleUnit($scope.config, null)
                                        .then(() => this.oncePerCycleRule())
                                        .then(() => {
                                            Utils.sendMessageToParent({
                                                name: 'configuratorMessage',
                                                data: {},
                                                correspondenceId: msg.correspondenceId,
                                            });
                                        });
                                });
                            });
                        } else if (messageName == 'getfields') {
                            let response: any = {};
                            this.rootConfig.getFields().forEach(f => {
                                response[f.name] = f.value;
                            });
                            Utils.sendMessageToParent({
                                name: 'configuratorMessage',
                                data: response,
                                correspondenceId: msg.correspondenceId,
                            });
                        } else if (messageName == 'setconfiguredproduct') {
                            $scope.$apply(() => {
                                return this.runConfiguredProduct(rootConfig, data).then(() => {
                                    if ($scope.deferLoadedRule && this.readyToStart) {
                                        this.model = { configuredProduct: data };
                                        this.readyToStart.resolve();
                                        this.readyToStart = null;
                                    }
                                    Utils.sendMessageToParent({
                                        name: 'configuratorMessage',
                                        data: {},
                                        correspondenceId: msg.correspondenceId,
                                    });
                                });
                            });
                        } else if (messageName == 'getconfiguredproduct') {
                            this.queueRule(() => {
                                Utils.sendMessageToParent({
                                    name: 'configuratorMessage',
                                    data: this.rootConfig.getConfiguredProduct(),
                                    correspondenceId: msg.correspondenceId,
                                });
                                return new KPromise(null) as any;
                            });
                        } else if (messageName == 'runaction') {
                            $scope.$apply(() => {
                                let action = this.rootConfig.actions.find(a => a.name.isEqual(msg.data));
                                $scope.runAction(action).then(() => {
                                    Utils.sendMessageToParent({
                                        name: 'configuratorMessage',
                                        data: {},
                                        correspondenceId: msg.correspondenceId,
                                    });
                                });
                            });
                        } else if (messageName == 'saveproductandsubmit') {
                            $scope.$apply(() => {
                                // clear out current quote first
                                quoteService.quote = null;
                                this.submit(true)
                                    .then(() => {
                                        return this.api.quotes
                                            .submit(this.quoteService.quote, this.$rootScope.contentTracker)
                                            .then(q => {
                                                // clear out the active quote again
                                                quoteService.quote = null;
                                                return q;
                                            });
                                    })
                                    .then(q => {
                                        Utils.sendMessageToParent({
                                            name: 'configuratorMessage',
                                            data: q,
                                            correspondenceId: msg.correspondenceId,
                                        });
                                    });
                            });
                        } else if (messageName == 'saveproduct') {
                            $scope.$apply(() => {
                                this.submit(true).then(() => {
                                    Utils.sendMessageToParent({
                                        name: 'configuratorMessage',
                                        data: this.quoteService.quote,
                                        correspondenceId: msg.correspondenceId,
                                    });
                                });
                            });
                        } else if (messageName == 'snapshot') {
                            if ($scope.activeSceneViewer) {
                                $scope.activeSceneViewer.snapshot(msg.data).then(result => {
                                    if (result) {
                                        Utils.sendMessageToParent({
                                            name: 'configuratorMessage',
                                            data: result,
                                            correspondenceId: msg.correspondenceId,
                                        });
                                    }
                                });
                            }
                        } else if (messageName == 'runsubmitrule') {
                            $scope.$apply(() => {
                                this.runSubmitRule().then(() => {
                                    Utils.sendMessageToParent({
                                        name: 'configuratorMessage',
                                        data: {},
                                        correspondenceId: msg.correspondenceId,
                                    });
                                });
                            });
                        } else if (messageName == 'getprice') {
                            $scope.$apply(() => {
                                this.runPricing().then(() => {
                                    Utils.sendMessageToParent({
                                        name: 'configuratorMessage',
                                        data: this.rootConfig.priceObject,
                                        correspondenceId: msg.correspondenceId,
                                    });
                                });
                            });
                        } else {
                            let messageArgs: IConfiguratorMessageRuleArgs = jQuery.extend(
                                this.getRuleArgs(this.rootConfig),
                                { message: msg }
                            );

                            if (this.rootConfig.hasRule(eRuleType.message)) {
                                return this.queueEventRuleType(this.rootConfig, eRuleType.message, messageArgs).then(
                                    () => {
                                        this.$scope.$digest();
                                    }
                                );
                            }
                        }
                    }
                }
            };
            $window.addEventListener('message', messageHandler);
            $window.addEventListener('keydown', $scope.keydown);

            $scope.$on('$destroy', () => {
                $window.removeEventListener('message', messageHandler);
                $window.removeEventListener('keydown', $scope.keydown);
            });

            // $scope.$watchCollection("config.actions", () => {
            //     $timeout(() => this.resizeActionBar(), 1);
            // });

            // this.resizeActionBar = Utils.throttle(() => {
            //     // $timeout(() => {
            //     let moreButtonsVisible = false;
            //     //let $actionBar = $(".kb-config__action-bar");
            //     let $header = $(".kb-config__header");
            //     let $moreButton = $(".kb-config__more-actions");

            //     // get more button width
            //     $moreButton.css("display", "inline-block");
            //     let moreButtonWidth = $moreButton.outerWidth();
            //     $moreButton.css("display", "");

            //     let available = $header.outerWidth() / 2 - moreButtonWidth; //$actionBar.outerWidth() - moreButtonWidth;
            //     let taken: number = 0;
            //     this.$scope.config.actions.forEach(action => {
            //         if (action.visible) {
            //             let $action = $("#action-" + action.id);
            //             let $moreAction = $("#action-more-" + action.id);
            //             // temporarily set the action to be visible even if it's
            //             // currently hidden because it's in the more menu
            //             $action.css("display", "inline-block");
            //             let actionWidth = $action.outerWidth();
            //             taken += actionWidth;

            //             let visibleInMore = (taken > available);
            //             if (visibleInMore) moreButtonsVisible = true;

            //             $action.css("display", visibleInMore ? "none" : "");
            //             $moreAction.css("display", visibleInMore ? "" : "none");
            //         }
            //     });
            //     $moreButton.css("display", moreButtonsVisible ? "" : "none");
            //     // });
            // }, 100);

            this.runResizeRule = Utils.debounce(() => {
                // run the resize rule of the configurator
                if (this.$scope.config.getRuleJs(eRuleType.resize) || this.$scope.config.getRuleJs(eRuleType.layout)) {
                    let args = this.getRuleArgs(this.$scope.config);
                    this.helper.runRuleTypeAsync(this.$scope.config, eRuleType.resize, args).then(() => {
                        if (this.$scope.config.hasRule(eRuleType.layout)) {
                            this.runLayoutRule(this.$scope.config, args).then(() => {
                                this.$scope.$digest();
                            });
                        }
                    });
                }
            }, 100);

            $(window).resize(() => {
                $timeout(() => {
                    this.runResizeRule();
                    //this.resizeActionBar();
                    this.refreshDrawer();
                }, 1);
            });

            // get pricing columns if relevant
            if (this.hasPricing()) {
                this.api.priceColumns.getPriceColumnsForUser().then(r => {
                    this.$scope.priceColumns = r;
                    this.$scope.priceColumnDb = {};
                    this.$scope.priceColumns.forEach(pc => (this.$scope.priceColumnDb[pc.name] = pc));
                    this.$scope.pricePrecision = this.$scope.priceColumns.find(
                        pc => pc.id == 14 /* <-- ExtNetPrice --> */
                    ).precision;
                });
            }

            this.drawerService.pageTitle = this.rootConfig.name;
            this.drawerService.pageType = loc.configurator;
            this.drawerService.pageIcon = icons.configurator;
            this.drawerService.showHelp = false;

            if (this.$scope.isTest) {
                if (rootConfig.builds.length) {
                    let drawers: IDrawer[] = [];
                    rootConfig.builds.forEach(b => {
                        drawers.push({
                            icon: icons.gear,
                            label: b.name,
                            command: () => {
                                $scope.testBuild(b);
                            },
                        });
                    });
                    drawerService.addDrawer(icons.test, loc.testbuild, () => {}, true, drawers);
                }
                drawerService.addDrawer(icons.test, loc.testpricing, () => {
                    let pricingArgs: IPricingRuleArgs = jQuery.extend(this.getRuleArgs(this.rootConfig), {
                        priceObject: {
                            items: [],
                            fields: {},
                        } as IPriceObject,
                        quoteCreator: this.$rootScope.user,
                        quoteOwner: this.$rootScope.user,
                    });
                    return this.helper.runRuleTypeAsync(this.rootConfig, eRuleType.pricing, pricingArgs);
                });
                drawerService.addDrawer(icons.name, loc.runnamingrule, () => {
                    this.helper.runRuleTypeAsync(this.rootConfig, eRuleType.naming);
                });
            }

            // this.drawerViewer = drawerService.addDrawer(icons.threeD, loc.threedview, () => {
            //     this.$scope.togglePages(false);
            // });
            // this.drawerViewer.classes = "visible-xs";
            // this.drawerOptions = drawerService.addDrawer(icons.group, loc.options, () => {
            //     this.$scope.togglePages(true);
            // });
            // this.drawerOptions.classes = "visible-xs";
            // this.drawerPricing = drawerService.addDrawer(icons.column, loc.details, () => {
            //     this.$scope.showPricing();
            // });
            // this.drawerPricing.classes = "visible-xs";

            this.drawerSubmitAndStay = drawerService.addDrawer(icons.save, $scope.getSubmitAndStayButtonText(), () => {
                this.submit(true);
            });

            this.drawerSubmit = drawerService.addDrawer(icons.quote, $scope.getSubmitButtonText(), () => {
                this.submit();
            });

            // this.drawerGenerateQrCode = drawerService.addDrawer(icons.add, $scope.getGenerateQrCodeText(), () => {
            //     this.qrCodeModal();
            // });

            if ($scope.isEdit) {
                if (this.$rootScope.isSfdcCpq) {
                    this.drawerSubmit.icon = icons.save;
                } else {
                    this.api.quotes.getById(this.model.idQuote).then(quote => {
                        $scope.parentQuote = quote;
                        if (this.$rootScope.isSfdc && !this.$rootScope.isSfdcCpq) {
                            quoteService.quote = quote;
                        }
                        if (
                            (!quote.idWorkflow && quote.createdBy == this.$rootScope.user.id) ||
                            quote.allowedActions.some(a => a.type == eWorkflowAction.modifyProducts)
                        ) {
                            let builds = quote.allowedActions
                                .filter(a => a.type == eWorkflowAction.buildQuoteProduct)
                                .filter(a => rootConfig.builds.find(b => b.idBuildType == a.buildType.id) != null);
                            if (builds.length) {
                                let drawers: IDrawer[] = [];
                                builds.forEach(action => {
                                    drawers.push({
                                        icon: icons.gear,
                                        label: action.buildType.name,
                                        command: () => {
                                            this.submit().then(() => {
                                                return this.api.quotes.buildQuoteProduct(
                                                    quote.id,
                                                    this.model.id,
                                                    action.buildType.id
                                                );
                                            });
                                        },
                                    });
                                });
                                drawerService.addDrawer(icons.build, 'Build', () => {}, true, drawers);
                            }
                        } else {
                            drawerService.drawers.remove(this.drawerSubmit);
                            drawerService.drawers.remove(this.drawerSubmitAndStay);
                            $scope.isReadOnly = true;
                            drawerService.addDrawer(icons.cancel, loc.back, () => {
                                this.$state.go('kb.quoteEdit', { id: this.model.idQuote });
                            });
                        }
                    });
                }

                //after running the configured product, nested configs could have been added to nested
                //sets.  We need to set the $nestedSetElement on those configs
                this.setAllNestedSetElements(rootConfig);
            }

            //this.runContinuousAsyncRule();

            // select the first page after defining the pagechanged handler
            this.selectNode(rootConfig.pages.find(p => p.visible == true));
            this.$scope.togglePages(true);

            this.readyToStart = $q.defer();

            this.startConfigurator();
        });
    }

    private runAsyncRule: boolean = true;

    private requestId: string;
    private model: IQuoteProduct;
    private readyToStart: ng.IDeferred<any>;
    public helper: ConfiguratorHelper;
    public rootConfig: Configurator;
    private activeRule: ng.IPromise<any>;
    private validationPromptDisplayed: boolean;

    // a cache of scene sessions in case the configurator loads the same scene multiple times
    public sceneDb: { [id: string]: IScene } = {};
    public productDb: { [id: number]: IProduct } = {};
    public translationDb: { [idProduct: number]: { [id: string]: ITranslationPackage } } = {};
    public sceneViewerDb: { [id: number]: SceneViewer } = {}; // holds a map of sceneViewer's to the scene id
    public loading: boolean = true; // whether the configurator is currently loading
    public configSession: IConfiguratorSession;
    public lastPricingPromise: ng.IPromise<any>;
    public lastViewerPromise: ng.IPromise<any>;
    public nextViewerPromise: ng.IPromise<any>;
    public sfdcCpqPayload: ISfdcCpqPayload;
    public drawerSubmit: IDrawer;
    public drawerSubmitAndStay: IDrawer;
    public drawerGenerateQrCode: IDrawer;
    public runResizeRule: () => void;
    public configuratorLoadedDeferred: angular.IDeferred<any>;

    public selectNode(tab: Page | Configurator) {
        this.$scope.selected.node = tab;
    }

    public navigateToElement(o: UiObject) {
        let animateTo: UiObject = null;
        let waitFor: number = 0;
        let waitPromise: PromiseLike<any> = new KPromise(null);

        if (o.visible) {
            if (o instanceof Page) {
                this.selectNode(o);
            } else if (o instanceof Configurator) {
                if (o.$parent instanceof NestedSet) {
                    let ancestorNestedSets = [o.$parent];
                    let p = o.$parent;
                    while (p) {
                        ancestorNestedSets.push(p);
                        if (p.$parentConfigurator.$parent instanceof NestedSet) {
                            p = p.$parentConfigurator.$parent;
                        } else {
                            p = null;
                        }
                    }

                    let page = ancestorNestedSets.last().findAncestor(kbo => kbo instanceof Page) as Page;
                    if (page && page.visible) {
                        this.selectNode(page);
                        animateTo = o;

                        for (let i = ancestorNestedSets.length - 1; i >= 0; i--) {
                            let nestedSet = ancestorNestedSets[i];

                            if (nestedSet.display == eNestedSetDisplay.accordion) {
                                waitPromise = waitPromise.then(() => {
                                    return this.$timeout(() => {
                                        if (!o['expanded']) waitFor = 350;
                                        o['expanded'] = true;
                                        return this.$timeout(); //we also need to wait for other expanders in the accordion to finish so we return another timeout
                                    });
                                });
                            } else if (nestedSet.display == eNestedSetDisplay.tabControl) {
                                nestedSet['$selectedTab'] = o.name;
                                animateTo = nestedSet;
                            }
                        }
                    }
                } else {
                    this.selectNode(o.pages.find(p => p.visible));
                }
            } else {
                let page = o.findAncestor(kbo => kbo instanceof Page) as Page;
                if (page && page.visible) {
                    this.selectNode(page);
                    animateTo = o;
                }
            }
            if (animateTo) {
                waitPromise.then(() => {
                    this.$timeout(() => {
                        let $uiObject = jQuery('.' + animateTo.$domId + ':visible');
                        if ($uiObject.length) {
                            Utils.scrollIntoView({
                                elem: $uiObject[0],
                                mode: eScrollMode.middleIfNecessary,
                                animate: true,
                            });
                        }
                    }, waitFor);
                });
            }
        }
    }

    public setAllNestedSetElements(config: Configurator) {
        //after running the configured product, nested configs could have been added to nested
        //sets.  We need to set the $nestedSetElement on those configs
        for (let nested of config.getAllConfigurators()) {
            this.handleNewConfigInNestedSet(nested, nested.$parent as Configurator | NestedSet | Page, false);
        }
    }

    public refreshDrawer() {
        if (this.$scope.isEmbed) {
            // only show the submit button if the configurator says it should be shown
            if (this.drawerSubmit)
                this.drawerSubmit.visible = this.drawerSubmitAndStay.visible = this.$scope.config.showSubmitButton;
        }
        if (this.$rootScope.isSfdcCpq) {
            if (this.drawerSubmit) {
                let isValid = this.$scope.config.isValid();
                this.drawerSubmit.disabled = !isValid;
                // if (!isValid) {
                //    this.drawerService.validationMessage = loc.validation_sfdc_correcterrors;
                // }
            }
            if (this.drawerSubmitAndStay) {
                this.drawerSubmitAndStay.visible = false;
            }
        }

        // if (this.drawerPricing) {
        //     this.drawerPricing.visible = (this.$rootScope.user && this.$rootScope.user.canViewPrices && this.$scope.config.showPrice);
        // }
        // let viewerExists = (this.$scope.viewerMode != eViewerMode.none);
        // if (this.drawerViewer) {
        //     this.drawerViewer.visible = viewerExists;
        //     this.drawerViewer.selected = !this.$scope.showPages;
        // }
        // if (this.drawerOptions) {
        //     this.drawerOptions.visible = viewerExists;
        //     this.drawerOptions.selected = this.$scope.showPages;
        // }
    }

    public loadSfdcCpqPayload(): ng.IPromise<any> {
        if (this.$rootScope.isSfdcCpq) {
            return this.sfdcService.getPayloadFromSfdcCpq().then(payload => {
                this.sfdcCpqPayload = payload;
                this.$scope.isEdit = this.sfdcCpqPayload.quoteProductId != null;
                this.$scope.isNew = !this.$scope.isEdit;
                this.$scope.sfdcHasCurrency = false;
                if (payload.data.quote.CurrencyIsoCode) {
                    this.$scope.sfdcCurrency = payload.data.quote.CurrencyIsoCode;
                    this.$scope.sfdcHasCurrency = true;
                }
            });
        } else {
            return this.$q.when(null);
        }
    }

    public loadModel(): ng.IPromise<any> {
        if (this.$scope.isEdit) {
            let quoteProductId = this.$rootScope.isSfdcCpq ? this.sfdcCpqPayload.quoteProductId : this.$stateParams.id;
            return this.api.quoteProducts.getById(quoteProductId, this.$rootScope.contentTracker).then(r => {
                this.model = r;
            });
        } else {
            this.model = {};
            return this.$q.when(null);
        }
    }

    /**
     * depending on the context, we need to figure out the product id for the configurator
     */
    public getProductId(): number {
        if (this.$rootScope.isSfdcCpq) {
            return this.$scope.isEdit ? this.model.idProduct : this.sfdcCpqPayload.productId;
        } else {
            return this.$scope.isEdit ? this.model.idProduct : this.$stateParams.id;
        }
    }

    protected addTranslationsForEntity(entity: {
        id?: number;
        translations?: IProductTranslation[] | ISceneTranslation[];
    }) {
        var trans = entity.translations.first();
        if (trans) {
            var entry = this.translationDb[entity.id];
            if (!entry) entry = this.translationDb[entity.id] = {};
            Object.keys(trans.data.objects).forEach(key => {
                entry[key] = trans.data.objects[key];
            });
        }
    }

    /**
     * This controller is used for both full configurators and scene configurators.
     * We load the configurator differently based on the situation
     */
    public getConfigurator(): ng.IPromise<Configurator> {
        let configuratorPromise: ng.IPromise<Configurator>;
        if (this.$scope.isScene) {
            // when we are running a scene configurator, the scene is embedded into the html
            let session = (window as any).serverVm.session as IConfiguratorSession;
            let scene = session.scenes.first();
            this.sceneDb[scene.id] = scene;
            this.configSession = { idScene: scene.id, scenes: [scene] };
            this.addTranslationsForEntity(scene);
            this.loadLayout(scene.configurator, session.layout);
            this.helper.loadTables(session.tables);
            let configurator = new Configurator(new KbObjectManager(), scene.configurator);
            configurator.$running = true;
            configuratorPromise = new KPromise(configurator) as any as ng.IPromise<Configurator>;
        } else {
            // it's a normal configurator
            configuratorPromise = this.authService.authPromise
                .then(() => this.loadSfdcCpqPayload())
                .then(() => this.loadModel())
                .then(() => {
                    let id = this.getProductId();
                    // if we are in test mode, we get the entire product with server rules
                    // like pricing rule(so they can be debugged on the client)
                    return this.api.products
                        .run(
                            id,
                            this.$scope.isTest,
                            this.$rootScope.environment != eEnvironment.dev, //whether to get the configSession pre-compressed
                            this.$rootScope.contentTracker
                        )
                        .then(
                            session => {
                                //special handling for nested parent
                                id = session.idProduct;
                                this.configSession = session;
                                // load the productDb
                                session.products.forEach(p => (this.productDb[p.id] = p));
                                // load the scene session db
                                session.scenes.forEach(s => (this.sceneDb[s.id] = s));
                                // load the translation db
                                session.products.forEach(p => {
                                    this.addTranslationsForEntity(p);
                                });
                                session.scenes.forEach(s => {
                                    this.addTranslationsForEntity(s);
                                });

                                return this.fillSourcedTables(session).then(() => {
                                    this.helper.loadTables(session.tables);

                                    // fill in the pages / nested configurators db
                                    this.$scope.userCanAddToDb = {};
                                    session.products.forEach(product => {
                                        this.$scope.userCanAddToDb[product.id] = {};
                                        if (product.configurator.referencedConfigurators) {
                                            product.configurator.referencedConfigurators.forEach(rc => {
                                                if (rc.userCanAddTo) {
                                                    rc.userCanAddTo.forEach(pageId => {
                                                        let entry =
                                                            this.$scope.userCanAddToDb[product.id][pageId] ||
                                                            (this.$scope.userCanAddToDb[product.id][pageId] = []);
                                                        entry.push({
                                                            id: rc.idProduct,
                                                            name: this.productDb[rc.idProduct].name,
                                                        });
                                                    });
                                                }
                                            });
                                        }
                                    });

                                    this.$scope.entity = this.productDb[id];
                                    let configurator = new Configurator(
                                        new KbObjectManager(),
                                        this.productDb[id].configurator
                                    );
                                    configurator.$running = true;
                                    this.$scope.showMove = this.$scope.showMove || configurator.showMoveAnimation;

                                    if (this.$scope.embedFields) {
                                        configurator.setFields(this.$scope.embedFields);
                                    }

                                    if (this.$scope.isEdit) {
                                        configurator.runConfiguredProduct(this.model.configuredProduct, this.productDb);
                                        configurator.sku = this.model.sku;
                                        configurator.name = this.model.name;
                                        configurator.description = this.model.description;
                                        configurator.shortDescription = this.model.shortDescription;
                                    }

                                    this.loadLayout(configurator, session.layout);

                                    return configurator;
                                });
                            },
                            err => {
                                this.dialogService.alert({
                                    type: eAlertType.error,
                                    msg: 'Product not found',
                                    persist: true,
                                });
                                this.$state.go('kb.products');
                                this.configuratorLoadedDeferred.reject('Product not found');
                                return null;
                            }
                        );
                });
        }
        return configuratorPromise;
    }

    protected loadLayout(rootConfig: Configurator | IConfigurator, layout: ILayout) {
        this.$scope.layoutConfig = new LayoutConfig(new KbObjectManager(), layout.layoutConfig);

        // load the layout settings into the layout
        if (rootConfig.layoutSettings) {
            rootConfig.layoutSettings.forEach(ls => {
                let field = this.$scope.layoutConfig.$manager.getByIdOrName<Field>(ls.id || ls.name, false);
                if (field) field.value = ls.value;
            });
        }

        //layout settings from the embed take precedence over those in the configurator properties screen, so we set them after
        if (this.$scope.embedLayoutSettings) {
            for (let lsname in this.$scope.embedLayoutSettings) {
                let field = this.$scope.layoutConfig.$manager.getByIdOrName<Field>(lsname, false);
                if (field) field.value = this.$scope.embedLayoutSettings[lsname];
            }
        }
    }

    /**
     * starts a configurator by loading any tables it references and running the rules once
     * @param config
     */
    public startConfigurator(): ng.IPromise<any> {
        this.readyToStart = this.$q.defer();
        if (this.$scope.deferLoadedRule) {
            Utils.sendMessageToParent({
                name: 'readyToLoadConfiguredProduct',
                data: {
                    requestId: this.requestId,
                },
            });
        } else {
            this.readyToStart.resolve();
        }
        return this.readyToStart.promise.then(() =>
            this.queueRule(() =>
                this.runLayoutRule(this.rootConfig, this.getRuleArgs(this.rootConfig))
                    .then(() =>
                        //start with layout rule so we don't get FOUC
                        this.nestedRunBottomsUp(this.rootConfig, c =>
                            this.runRuleCycleUnit(c, null).then(() => {
                                // run the loaded rule
                                let args = this.getLoadedRuleArgs(c);
                                let origNumberOfNesteds = c.getNestedConfigurators().length;
                                return this.helper.runRuleTypeAsync(c, eRuleType.resize, args).then(() => {
                                    return (
                                        this.runLoadedRule(c, args)
                                            // if the loaded rule changed field values or added nested
                                            // configs we need to run the rule cycle again
                                            .then(() => {
                                                if (
                                                    c.$fieldsDirty ||
                                                    origNumberOfNesteds != c.getNestedConfigurators().length
                                                ) {
                                                    return this.runRuleCycleUnit(c, null);
                                                }
                                            })
                                    );
                                });
                            })
                        )
                    )
                    .then(() => {
                        this.loading = false;
                        return this.oncePerCycleRule();
                    })
                    .then(() => {
                        this.configuratorLoadedDeferred.resolve();
                        Utils.sendMessageToParent({
                            name: 'configuratorLoaded',
                            data: {
                                requestId: this.requestId,
                            },
                        });
                    })
            )
        );
    }

    public saveToQuote(): ng.IPromise<IQuoteProduct> {
        //!!!Should only be called by "submit".  Everything else should call submit() which calls this
        if (this.$rootScope.isSfdcCpq) {
            // if steelbrick, then add the product to a blank quote and submit it
            let quote = this.quoteService.getNewQuote();
            quote.externalId = this.sfdcCpqPayload.data.quote.Id;
            // name the quote the same as the sfdc cpq quote for tracking
            // in version 28.0.1 of steelbrick they took away the record property
            quote.name = (this.sfdcCpqPayload.data.quote as any).record
                ? (this.sfdcCpqPayload.data.quote as any).record.Name
                : this.sfdcCpqPayload.data.quote.Name;

            quote.products = [
                {
                    idProduct: this.$scope.entity.id,
                    qty: 1,
                    configuredProduct: this.rootConfig.getConfiguredProduct(),
                },
            ];
            return this.api.quotes.save(quote, this.$rootScope.contentTracker).then(q => {
                // add the quoteproduct id to the payload so it can be saved in sfdc cpq
                this.sfdcCpqPayload.quoteProductId = q.products[0].id;
                return q.products[0];
            });
        } else if (this.$scope.isNew) {
            return this.quoteService
                .addProduct({
                    idProduct: this.$scope.entity.id,
                    qty: 1,
                    configuredProduct: this.rootConfig.getConfiguredProduct(),
                    addLocally: true,
                    silent: this.$scope.isEmbed, // don't show confirmation dialog if we are in an embed
                    currency: this.$scope.getCurrency(),
                    sku: this.model.sku
                })
                .then(r => {
                    return r;
                });
        } else {
            this.model.configuredProduct = this.rootConfig.getConfiguredProduct();
            return this.api.quotes
                .editQuoteProduct(this.model.idQuote, this.model, this.$rootScope.contentTracker)
                .then(r => {
                    const result = this.quoteService.getLatestQuoteProduct(r);
                    return result;
                });
        }
    }

    private setSubmitButtonDisabled(disabled: boolean) {
        this.drawerSubmitAndStay.disabled = this.drawerSubmit.disabled = disabled;
    }

    public submit(stayOnPage: boolean = false): ng.IPromise<any> {
        this.setSubmitButtonDisabled(true);
        let deferred = this.$q.defer<IQuoteProduct>();
        let promise = deferred.promise;
        this.validationPromptDisplayed = false;

        /* 
            Process validation before AND after running the submit rule.
            We run it before because in some scenarios people are adding nested configurators in the submit rule,
            and if we're not valid already, then we'd rather not add them.
            We run it after too because perhaps the submit rule changed something that makes it invalid.
        */
        this.shouldSubmit().then(should => {
            if (should) {
                this.runSubmitRule().then(() => {
                    this.shouldSubmit().then(should => {
                        if (should) {
                            this.saveToQuote().then(r => {
                                deferred.resolve(r);
                            });
                        }
                        else {
                            deferred.reject();
                        }
                    });
                });
            } else {
                deferred.reject();
            }
        });

        return promise
            .then(
                data => {
                    this.telemetryService.trackEvent('Configurator Submit');
                    if (this.$rootScope.isSfdcCpq) {
                        this.sfdcService.saveToSfdcCpq(this.sfdcCpqPayload, data.priceObject);
                    } else if (this.$scope.isNew && !this.$scope.isEmbed) {
                        if (stayOnPage) {
                            this.$state.go('kb.configuredProduct', { id: data.id });
                        } else {
                            this.quoteService.goToQuote();
                        }
                    } else if (this.$scope.isEdit) {
                        this.$scope.$broadcast('saveSuccess', data);
                        if (!stayOnPage) this.$state.go('kb.quoteEdit', { id: this.model.idQuote });
                    }
                },
                reason => {
                    this.telemetryService.trackEvent('Configurator Submit Rejected');
                    this.setSubmitButtonDisabled(false);
                    return this.$q.reject(reason);
                }
            )
            .finally(() => {
                if (stayOnPage)
                    this.setSubmitButtonDisabled(false);
            });
    }

    public qrCodeModal() {
        const dialogScope: IGenerateQrCodeScope = this.$rootScope.$new();
        dialogScope.autoStartAr = false;
        let url = window.location.href;
        if (url.includes('ar=true')) {
            dialogScope.autoStartAr = true;
        }
        dialogScope.updateQrCode = newVal => {
            if (newVal !== dialogScope.autoStartAr) {
                dialogScope.autoStartAr = newVal;
            }
        };
        const canvas = document.createElement('canvas');
        QRCode.toCanvas(canvas, url, { width: 150 }, function (error) {
            if (error) console.error(error);
        });
        dialogScope.QrCode = canvas;

        this.dialogService.dialog({
            template: Dirs.view('dialog-create-qr-code', eBundle.app),
            scope: dialogScope,
            buttons: [new InputDialogButton(loc.cancel, icons.cancel, args => {})],
        });

        dialogScope.$watch(
            'autoStartAr',
            function (newVal) {
                if (newVal === true) {
                    if (url.includes('ar=true') === false) {
                        url = url + '?ar=true';
                    }

                    window.history.pushState({}, null, url);
                    QRCode.toCanvas(canvas, url, { width: 150 }, function (error) {
                        if (error) console.error(error);
                    });
                } else {
                    if (url.includes('ar=true') === true) {
                        url = url.replace('ar=true', '');
                        if (url.charAt(url.length - 1) === '?') {
                            url = url.slice(0, -1);
                        }
                    }

                    window.history.pushState({}, null, url);
                    QRCode.toCanvas(canvas, url, { width: 150 }, function (error) {
                        if (error) console.error(error);
                    });
                }
                const panel: HTMLElement = document.getElementById('qrCodeCanvas');
                if (panel instanceof HTMLCanvasElement) {
                    const context = panel.getContext('2d');
                    context.clearRect(0, 0, canvas.width, canvas.height);
                    context.drawImage(canvas, 0, 0);
                    //panel.append(canvas);
                }
                //dialogScope.QrCode = canvas;
            },
            true
        );

        setTimeout(() => {
            const panel: HTMLElement = document.getElementById('qrCodeCanvas');
            QRCode.toCanvas(panel, url, { width: 150 }, function (error) {
                if (error) console.error(error);
            });
            panel.append(canvas);
        }, 1000);
    }

    public enterArModal() {
        const dialogScope = this.$rootScope.$new();
        const enterArButton = new InputDialogButton('Enter AR', icons.change, args => {
            if (this.$scope.activeSceneViewer) {
                this.$scope.activeSceneViewer.$scope.toggleAr();
            }
        });
        this.dialogService.dialog({
            scope: dialogScope,
            buttons: [enterArButton, new InputDialogButton(loc.cancel, icons.cancel, args => {})],
        });
    }

    public shouldSubmit(): ng.IPromise<boolean> {
        let deferred = this.$q.defer<boolean>();

        if (this.rootConfig.isValid()) {
            deferred.resolve(true);
        } else {
            //it's not valid
            if (this.rootConfig.allowSaveWithErrors) {
                if (!this.validationPromptDisplayed) {
                    this.validationPromptDisplayed = true;
                    this.dialogService.dialog({
                        content: loc.msg_savetoquoteerrors,
                        buttons: [
                            new DialogButton(loc.save, icons.success, args => {
                                deferred.resolve(true);
                            }),
                            new DialogButton(loc.keepworking, icons.cancel, args => {
                                deferred.resolve(false);
                            }),
                        ],
                    });
                } else {
                    deferred.resolve(true);
                }
            } else {
                this.dialogService.alert({
                    msg: loc.msg_fixerrorsbeforesave,
                    persist: false,
                    type: eAlertType.error,
                });
                deferred.resolve(false);
            }
        }

        return deferred.promise;
    }

    public runConfiguredProduct(configurator: Configurator, configProd: IConfiguredProduct) {
        return this.queueRule(() => {
            configurator.runConfiguredProduct(configProd, this.productDb);
            this.setAllNestedSetElements(configurator);
            return this.nestedRunBottomsUp(configurator, c => this.runRuleCycleUnit(c, null)).then(() =>
                this.oncePerCycleRule()
            );
        });
    }

    public addNestedConfig(
        idNested: number,
        parent: Page | Configurator | NestedSet,
        addedByUser: boolean,
        name: string,
        fields?: {}
    ): ng.IPromise<any> {
        let product = this.productDb[idNested];

        // if a configurator with the given name already exists, then we do nothing
        let nested = parent.configurators.find(c => c.name.isEqual(name));
        if (!nested) {
            nested = new Configurator(new KbObjectManager(), product.configurator);
            nested.$running = true;
            if (name) nested.name = name;
            parent.$expanded = true;
            parent.configurators.push(nested);
            parent.$manager.addKbObject(nested, parent);
            if (addedByUser) nested.addedByUser = this.$rootScope.user.id;

            if (fields) {
                nested.setFields(fields);
            }

            let parentConfig = parent instanceof Configurator ? (parent as Configurator) : parent.$parentConfigurator;
            this.handleNewConfigInNestedSet(nested, parent);

            if (this.sceneDb[nested.idScene] && this.sceneDb[nested.idScene].external) {
                nested.nestedPath = nested.idScene;
            } else {
                nested.nestedPath = this.getConfiguratorUniqueKey(nested);
            }

            // run the added rule on the nested configurator
            return this.runNestedRuleType(parentConfig, nested, eRuleType.configuratorAdded)
                .then(() => this.runRuleCycleUnit(nested, null, false)) // don't bubble here
                .then(() => nested);
        } else if (fields) {
            nested.setFields(fields); //no need to run
            if (nested.$fieldsDirty) {
                return this.runRuleCycleUnit(nested, null, false).then(() => nested);
            } else {
                return new KPromise(nested) as any;
            }
        } else {
            return new KPromise(nested) as any;
        }
    }

    public getConfiguratorUniqueKey(config: Configurator) {
        let looping = true;
        do {
            let nestedSettings = undefined;
            if (config.$parentConfigurator) {
                nestedSettings = config.$parentConfigurator.referencedConfigurators.find(rc => rc.name === config.name);
            }
            if (nestedSettings && nestedSettings.nestScene) {
                config = config.$parentConfigurator;
            } else {
                looping = false;
            }
        } while (looping);

        if (config.$parentConfigurator) {
            return config.$parentConfigurator.name + '/' + config.name;
        } else {
            return config.name;
        }
    }

    public copyNestedConfig(
        sourceConfig: Configurator,
        appendTo: Page | Configurator | NestedSet,
        addedByUser: boolean,
        name?: string
    ) {
        let product = this.productDb[sourceConfig.idProduct];

        let target = new Configurator(new KbObjectManager(), product.configurator);
        target.$running = true;
        target.runConfiguredProduct(sourceConfig.getConfiguredProduct(), this.productDb);
        target.name = name || product.configurator.name; // get default name

        appendTo.$expanded = true;
        appendTo.configurators.push(target);
        appendTo.$manager.addKbObject(target, appendTo);
        if (addedByUser) target.addedByUser = this.$rootScope.user.id;
        this.handleNewConfigInNestedSet(target, appendTo);

        // run the added rule on the nested configurator
        return this.runNestedRuleType(sourceConfig.$parentConfigurator, target, eRuleType.configuratorCopied)
            .then(() => this.nestedRunBottomsUp(target, c => this.runRuleCycleUnit(c, null)))
            .then(() => this.oncePerCycleRule())
            .then(() => target);
    }

    protected handleNewConfigInNestedSet(
        nested: Configurator,
        parent: Page | Configurator | NestedSet,
        navigateTo: boolean = true
    ) {
        //we need to save a handle to the element chosen to represent the nested set so we can access it in our angular view
        if (parent && parent instanceof NestedSet) {
            //get the referenced configurator
            let refConfig = parent.$parentConfigurator.referencedConfigurators.find(
                rc => rc.idProduct == nested.idProduct
            );
            nested.$nestedSetElement = nested.$manager.get(refConfig.element);
            if (parent.visible && parent.autoNavigateToElement && navigateTo) {
                this.$timeout(() => {
                    this.navigateToElement(nested);
                });
            }
        }
    }

    public removeNestedConfig(nested): ng.IPromise<any> {
        return new KPromise(nested).then(() => {
            if (nested) {
                // might be a page or configurator passed in.  If it's a page,
                // it's the page's parent configurator that needs to be deleted
                if (nested instanceof Page || nested instanceof NestedSet)
                    nested = nested.$parentConfigurator as Configurator;

                // we need a new selection if we are deleting the page we're on
                if (this.$scope.selected.page) {
                    if (this.$scope.selected.page.findAncestor(a => a == nested)) {
                        if (nested.$parent instanceof Page) {
                            this.selectNode(nested.$parent as Page);
                        } else {
                            this.selectNode((nested.$parent as Configurator).pages.find(p => p.visible));
                        }
                    }
                }

                nested.$parent.$manager.removeKbObject(nested);
                (nested.$parent as Page | Configurator | NestedSet).configurators.remove(nested as Configurator);

                let parentConfig =
                    nested.$parent instanceof Configurator
                        ? (nested.$parent as Configurator)
                        : nested.$parent.$parentConfigurator;

                // run the removed rule on the nested configurator
                return this.runNestedRuleType(parentConfig, nested as Configurator, eRuleType.configuratorRemoved);
            }
        }) as any;
    }

    public setNumberOfNestedConfigs(
        productId: number,
        parent: Page | Configurator | NestedSet,
        num: number
    ): ng.IPromise<any> {
        let product = this.productDb[productId];
        let configs = parent.configurators.filter(c => c.idProduct == productId);
        let promises: ng.IPromise<any>[] = [];
        let configsChanged: boolean = false;
        if (configs.length < num) {
            // need to add
            for (let i = configs.length; i < num; i++) {
                configsChanged = true;
                promises.push(this.addNestedConfig(productId, parent, false, null));
            }
        } else if (configs.length > num) {
            // need to remove
            for (let i = configs.length - 1; i >= num; i--) {
                configsChanged = true;
                promises.push(this.removeNestedConfig(configs[i]));
            }
        }
        let prom: ng.IPromise<any> = this.$q.all(promises);
        if (configsChanged) {
            let parentConfig = parent instanceof Configurator ? (parent as Configurator) : parent.$parentConfigurator;
            prom = prom.then(() => this.runRuleCycleUnit(parentConfig, null));
        }
        return prom;
    }

    protected getChildrenForNode(tabNode: ITabNode): ITabNode[] {
        let nodes: ITabNode[] = [];
        tabNode.node.pages.forEach(p => {
            if (p.visible && !this.$scope.selected.isSingleNode(p.$parentConfigurator)) {
                let child = { id: p.id, level: tabNode.level + 1, node: p };
                nodes.push(child);
                nodes.pushArray(this.getChildrenForNode(child));
            }
        });
        tabNode.node.configurators.forEach(c => {
            let child = { id: c.id, level: tabNode.level + 1, node: c };
            nodes.push(child);
            nodes.pushArray(this.getChildrenForNode(child));
        });
        return nodes;
    }

    public calculateNodes() {
        let rootNode: ITabNode = { id: this.rootConfig.id, level: -1, node: this.rootConfig };
        this.$scope.nodes = [];
        this.$scope.nodes.pushArray(this.getChildrenForNode(rootNode));

        this.$scope.nextNode = this.getNextNode(this.$scope.selected.page, false);
        this.$scope.backNode = this.getBackNode(this.$scope.selected.node);
    }

    public getNextNode(node: Page | Configurator, skipChildren: boolean) {
        let next: Page | Configurator;
        if (node) {
            let parent = node.$parent as Page | Configurator;
            if (!skipChildren) {
                // first find if there is a nested page
                next = node.pages.find(p => p.visible);
                // then check nested configurators
                if (!next) next = node.configurators.find(c => c.pages.some(p => p.visible));
            }
            // get next page or configurator with the same parent
            if (!next && parent) {
                if (node instanceof Page) {
                    next = parent.pages.next(node, p => p.visible);
                }
                if (!next) next = parent.configurators.next(node as Configurator, c => c.pages.some(p => p.visible));
            }

            if (!next && parent) next = this.getNextNode(parent, true);
        }
        return next;
    }

    public getBackNode(node: Page | Configurator) {
        let prev: Page | Configurator;

        if (node) {
            let parent = node.$parent as Page | Configurator;

            // find the previous item
            if (node instanceof Configurator) {
                prev = parent.configurators.previous(node, c => c.pages.some(p => p.visible));
            }
            if (!prev) prev = parent.pages.previous(node as Page, p => p.visible);

            if (prev) {
                let n = prev;
                while (n) {
                    // find the last nested child
                    n = prev.configurators.filter(c => c.pages.some(p => p.visible)).last();
                    if (!n) n = prev.pages.filter(p => p.visible).last();
                    if (n) prev = n;
                }
            } else {
                // if there was no siblings, then go to the parent
                if (!prev && parent != this.rootConfig) prev = parent;
                if (prev instanceof Configurator) prev = this.getBackNode(prev);
            }
        }
        return prev;
    }

    public queueRule<TRes>(ruleAction: () => ng.IPromise<TRes>) {
        if (this.activeRule) {
            this.activeRule = this.activeRule.then(() => {
                return ruleAction();
            });
        } else {
            this.activeRule = ruleAction();
        }
        return this.activeRule;
    }

    public oncePerCycleRule(): ng.IPromise<any> {
        return new KPromise(
            (() => {
                this.runPricing();
                this.calculateNodes();
                this.getCurrentConfigForViewerAndSetViewerMode(); //need to set viewerMode before layout rule so the layout rule can show/hide the viewer accordingly
            })()
        )
            .then(() => this.runLayoutRule(this.$scope.config, this.getRuleArgs(this.$scope.config)))
            .then(() => !this.loading && this.refreshViewer())
            .then(() => {
                this.$scope.actionsVisible = this.$scope.config.actions.some(a => a.visible);
                this.$timeout(() => {
                    //this.resizeActionBar();
                    this.refreshDrawer();
                });
            }) as any;
    }

    private runLoadedRule(config: Configurator, args: ILoadedRuleArgs): Q.IPromise<IJsResult<ILoadedRuleArgs>> {
        if (!this.$scope.isScene && !config.$ranLoadedRule) {
            //scene loaded rule is handled by the viewer
            config.$ranLoadedRule = true;
            return this.helper.runRuleTypeAsync(config, eRuleType.loaded, args);
        }
        return new KPromise(<IJsResult<ILoadedRuleArgs>>{ parameters: args, hasError: false });
    }

    private runningRules: any = {};
    private static cycleLimit: number = 2;
    /**
     * manages bubbling the value rule running up or down.  We do this isolated,
     * so we don't end up over-running other rules (like validation, pricing, scene).
     * Returns a boolean indicating whether the configurator fields were dirty immediately
     * after running the value rule (indicating whether something before or during the value
     * rule actually modified a field)
     * @param config
     * @param field
     */
    public runRuleCycleUnit(config: Configurator, field: Field, bubble = true): ng.IPromise<boolean> {
        if (this.runningRules[config.name] >= ConfiguratorController.cycleLimit) {
            console.log(`Rule cycle recursion limit hit for configurator '${config.name}'.  Opting out of running`);
            return this.$q.when(false);
        }

        if (!this.runningRules[config.name]) this.runningRules[config.name] = 1;
        else this.runningRules[config.name]++;

        let nestedRulesRan = false;
        let ruleArgs = this.getRuleArgs(config, field);
        let origNumberOfNesteds = config.getNestedConfigurators().length;
        let configWasDirty = false;

        let runRules = this.helper
            .runSelects(config)
            // value rules
            .then(() => this.helper.runRuleTypeAsync(config, eRuleType.value, ruleArgs))
            .then(() => {
                configWasDirty = config.$fieldsDirty;
                // reset $fieldsDirty of this configurator so it doesn't affect a
                // parent's decision on whether to run the child's rules
                config.$fieldsDirty = false;
            })
            .then(() => {
                // run rule cycle on dirty nesteds
                let promises: ng.IPromise<any>[] = [];
                config.getNestedConfigurators().forEach(nested => {
                    /* if the nested configurator was changed by the parent rules OR
                           if the nested is setup to be always a child, and this parent actually had a change
                           then we need to run the nested's value rule */
                    if (nested.$fieldsDirty || (nested.alwaysChild && nested.alwaysChildOf && configWasDirty)) {
                        nestedRulesRan = true;
                        // don't bubble the nested rules here.  We'll take care of it at the parent level next
                        promises.push(this.runRuleCycleUnit(nested, null, false));
                    }
                });
                return this.$q.all(promises);
            })
            .then(
                (dirtyArr: boolean[]) =>
                    // after running nested config rule cycles, if this parent is referencing them,
                    // it now needs to be updated we do this here instead of using the bubbling below
                    // so that the parent only re - runs it's rules once, instead of once for each nested
                    nestedRulesRan &&
                    ((!this.loading && dirtyArr && dirtyArr.some(b => b == true)) || //make sure that one of the nesteds is actually dirty or it's pointless to re-run this config's value rule
                        (this.loading && origNumberOfNesteds < config.getNestedConfigurators().length)) && // if we are loading, then only bubble if a new nested configurator was added by the value rule
                    config.bubbleRules &&
                    this.helper.runRuleTypeAsync(config, eRuleType.value, ruleArgs)
            )
            .then(() => {
                return this.runValidationRule(config, ruleArgs);
            })
            .then(() => this.helper.runRuleTypeAsync(config, eRuleType.visibility, ruleArgs))
            .then(
                () =>
                    // as opposed to the bubbling above, this bubbling is more for the user having changed
                    // a field value in the nested configurator directly and us sensing that the parent now
                    // needs to update. (only if we are done loading, since loading bubbles through all
                    // configurators anyways)
                    !this.loading &&
                    bubble &&
                    config.$parentConfigurator &&
                    config.$parentConfigurator.bubbleRules &&
                    this.runRuleCycleUnit(config.$parentConfigurator, null)
            )
            .then(() => {
                delete this.runningRules[config.name];
            });

        return runRules.then(() => configWasDirty);
    }

    public queueEventRuleType(config: Configurator, ruleType: eRuleType, args: IConfiguratorRuleArgs) {
        let rc = config.ruleContainers.find(rc => {
            return rc.ruleType.isEqual(ruleType);
        });
        return this.queueEventRule(config, rc, args);
    }

    /** wraps the given delegate to run a rule, and follows it with a rule cycle if the event rule changed something that warrants it */
    public queueEventRule(config: Configurator, ruleContainer: RuleContainer, args: IConfiguratorRuleArgs) {
        return this.queueRule(() => {
            //if there is no rule for the rule type, then cut it off here so we don't run oncePerCycleRule unnecessarily
            if (ruleContainer && ruleContainer.js) {
                let origNumberOfNesteds = config.getNestedConfigurators().length;
                return this.helper.runRuleContainerAsync(ruleContainer, args).then(() => {
                    let nesteds = config.getNestedConfigurators();
                    if (
                        config.$fieldsDirty ||
                        (this.$scope.layoutConfig && this.$scope.layoutConfig.$layoutSettingsDirty) ||
                        origNumberOfNesteds != nesteds.length ||
                        nesteds.some(n => n.$fieldsDirty)
                    ) {
                        return this.runRuleCycleUnit(config, null).then(() => this.oncePerCycleRule());
                    } else if (
                        ruleContainer.ruleType.isEqual(eRuleType.tabChanged) &&
                        ruleContainer.references.length
                    ) {
                        this.refreshViewer(true);
                    } else {
                        this.$scope.$digest(); //because of the way event queueing works, we need to $digest here
                    }
                });
            } else {
                let result: IJsResult<IConfiguratorRuleArgs> = {
                    hasError: false,
                    parameters: args,
                };
                return new KPromise(result) as any;
            }
        });
    }

    /**
     * Runs the given function on the configurator given and all of it's child configurators in the direction given
     * @param fn
     */
    public nestedRunTopDown(config: Configurator, fn: (nested: Configurator) => ng.IPromise<any>) {
        let nesteds = config.getNestedConfigurators();
        return fn(config).then(() => this.$q.all(nesteds.map(n => this.nestedRunTopDown(n, fn))));
    }

    public nestedRunBottomsUp(config: Configurator, fn: (nested: Configurator) => ng.IPromise<any>): ng.IPromise<any> {
        let nesteds = config.getNestedConfigurators();
        let continueRun = () => this.$q.all(nesteds.map(n => this.nestedRunBottomsUp(n, fn)));
        if (this.loading && nesteds.some(n => n.alwaysChild)) {
            return this.nestedRunTopDown(config, fn)
                .then(continueRun)
                .then(() => fn(config));
        } else {
            return continueRun().then(() => fn(config));
        }
    }

    public runNestedRuleType(
        config: Configurator,
        nested: Configurator,
        ruleType: string,
        args?: INestedConfiguratorRuleArgs
    ): ng.IPromise<IJsResult<IConfiguratorRuleArgs>> {
        if (!args) args = this.getRuleArgs(config);
        args.nested = nested;

        let refConfig = config.referencedConfigurators.find(rc => rc.idProduct == nested.idProduct);
        let ruleContainer = refConfig.ruleContainers.find(rc => rc.ruleType.isEqual(ruleType));
        if (ruleContainer) {
            return this.helper.runRuleContainerAsync(ruleContainer, args);
        } else {
            return this.$q.when(null);
        }
    }

    public runValidationRule(config: Configurator, ruleArgs: IConfiguratorRuleArgs): ng.IPromise<any> {
        config.preValidate(); // call internal validation for fields
        return this.helper.runRuleTypeAsync(config, eRuleType.validation, ruleArgs).then(() => {
            config.isValid(); //force recalculation of $valid property
        });
    }

    public runLayoutRule(config: Configurator, ruleArgs: IConfiguratorRuleArgs): ng.IPromise<any> {
        return this.helper.runRuleTypeAsync(this.$scope.config, eRuleType.layout, ruleArgs).then(() => {
            this.$scope.layoutConfig.$layoutSettingsDirty = false;
            if (!this.$scope.layoutLoaded) {
                this.$scope.layoutLoaded = true;
                if (this.$scope.config.hasRule(eRuleType.layout)) {
                    /**
                     * Process UI changes when first running so there is no FOUC.
                     * Note that we only call this if there is actually a layout rule, because
                     * otherwise the runRuleTypeAsync above actually resolves synchronously because it's
                     * a KbPromise, then we get an error on the $digest call that there is already one in
                     * progress
                     */
                    this.$scope.$digest();
                }
            }

            return this.$scope.resizeScene();
        });
    }

    public runSubmitRule(): ng.IPromise<any> {
        console.log('runSubmitRule');
        this.runAsyncRule = false;
        this.rootConfig.parameters = this.getParameters();

        return this.queueRule(() => {
            return this.nestedRunBottomsUp(this.rootConfig, c => {
                let origNumberOfNesteds = c.getNestedConfigurators().length;
                let ruleArgs = this.getLoadedRuleArgs(c);
                //set all fields to be "touched" and re-run validation to satisfy configurator.validationTiming setting
                c.getFields().forEach(f => (f.hasBeenTouched = true));

                return this.runValidationRule(c, ruleArgs).then(() => {
                    return this.helper.runRuleTypeAsync(c, eRuleType.submit, ruleArgs).then(() => {
                        let nesteds = c.getNestedConfigurators();
                        //if the submit rule changed any fields or added nested configurators, run another rule cycle
                        if (
                            c.$fieldsDirty ||
                            origNumberOfNesteds != nesteds.length ||
                            nesteds.some(n => n.$fieldsDirty)
                        ) {
                            return this.runRuleCycleUnit(c, null);
                        }
                    });
                });
            }).then(() => {
                this.telemetryService.trackEvent('Configurator Submit Rule Run');
            });
        }).then(() => {
            const deferred = this.$q.defer();
            const configurator = this.configSession.products[0].configurator;
            const completeSubmit = () => {
                if (configurator.kineticSettings.notUnique) {
                    const partNum = this.model.sku;
                    return this.api.kinetic.partNumExists(partNum).then(exists => {
                        if (exists) {
                            this.dialogService.alert({
                                msg: "Part " + partNum + " already exists",
                                persist: true
                            });
                        }
                        deferred.resolve();
                    });
                }
                else {
                    deferred.resolve();
                }
            }
            // Disables this codepath, which is not going to be supported for now.
            if (configurator.kineticSettings) configurator.kineticSettings.createPart = false;
            if (configurator.kineticSettings && configurator.kineticSettings.createPart) {
                if (configurator.kineticSettings.autoCrtPart) {
                    const configuration = " configuration = " + this.model.name;
                    const part = this.model.sku ? " part = " + this.model.sku : "";

                    this.dialogService.confirm("Would you like to create a new part number for" + configuration + part + "?", () => {
                        if (configurator.kineticSettings.crtPartUsing == "PromptFor") {
                            this.dialogService.input({
                                inputType: eInputDialogType.text,
                                msg: "Enter your desired part number",
                                value: "",
                                buttons: [
                                    new InputDialogButton(loc.ok, icons.success, args => {
                                        if (args.value == "") {
                                            args.close = false;
                                        }
                                        else {
                                            this.model.sku = args.value;
                                            completeSubmit();
                                        }
                                    }),
                                    new InputDialogButton(loc.cancel, icons.cancel, args => {
                                        this.drawerSubmit.disabled = false;
                                        this.drawerSubmitAndStay.disabled = false;
                                    })
                                ]
                            });
                        }
                        else {
                            completeSubmit();
                        }
                    }, () => {
                        completeSubmit();
                    }, loc.yes, loc.no);
                    return deferred.promise;
                }
                else {
                    completeSubmit();
                }
            }
        });
    }

    public showErrors() {
        return (
            this.$scope.isScene ||
            this.$scope.isTest ||
            this.$rootScope.environment == eEnvironment.dev ||
            this.$rootScope.environment == eEnvironment.test
        );
    }

    public getRuleArgs(config: Configurator, changedField?: Field): ConfiguratorRuleArgs {
        let ruleArgs = new ConfiguratorRuleArgs(this);
        ruleArgs.user = this.$rootScope.user;
        ruleArgs.environment = this.$rootScope.environment;
        ruleArgs.baseUrl = this.$rootScope.context.baseUrl;
        ruleArgs.configurator = config;
        ruleArgs.layoutConfig = this.$scope.layoutConfig;
        ruleArgs.kom = config.$manager;
        ruleArgs.parentKom = config.$parentConfigurator ? config.$parentConfigurator.$manager : null;
        ruleArgs.changedField = changedField;
        ruleArgs.logs = new ClientLogger();
        ruleArgs.clientLanguage = this.$rootScope.clientLanguage;
        ruleArgs.viewerMode = this.$scope.viewerMode;
        ruleArgs.isCrm = this.$rootScope.isSfdc || this.$rootScope.isSfdcCpq;

        ruleArgs.parameters = this.getParameters();

        return ruleArgs;
    }

    public getLoadedRuleArgs(config: Configurator): IConfiguratorLoadedRuleArgs {
        let args = jQuery.extend(this.getRuleArgs(config), {
            isEdit: this.$scope.isEdit || (this.$scope.isKinetic && this.$scope.deferLoadedRule),
            quoteProduct: this.$scope.isEdit || this.$scope.deferLoadedRule ? this.model : null,
        });
        return args;
    }
    public createSceneViewer(scene: IScene): ng.IPromise<SceneViewer> {
        let sceneViewer: SceneViewer;
        let viewerScope = this.$scope.$new();
        const company = this.$rootScope.company;
        const env = this.$rootScope.environment;

        let vArgs = {
            //$rootScope: this.$rootScope,
            //$http: this.$http,
            environment: this.$rootScope.environment,
            baseUrl: this.$rootScope.context.baseUrl,
            assetRoot: this.$rootScope.context.publicStorageUrl,
            clusterEnv: this.$rootScope.context.clusterEnv,
            cacheBuster: this.$rootScope.context.cacheBuster,
            clientLanguage: this.$rootScope.clientLanguage,
            isMobile: this.$rootScope.windowWidth < Utils.MOBILE_WIDTH,
            isRender: false,
            renderPass: null,
            $q: this.$q,
            $timeout: this.$timeout,
            $injector: this.$injector,
            ruleService: this.ruleService,
            //uploadService: this.uploadService,
            dialogService: this.dialogService,
            highlightColor: this.themeService.getActiveTheme().accent,
            //$compile: this.$compile,
            $scope: viewerScope,
            handleRuleError: (ruleName, result) => this.helper.handleRuleError(ruleName, result),
            tableArraysDb: this.helper.tableArraysDb,
            //helper: this.helper,
            elemSelector: '.kb-viewer',
            //isEmbed: true,
            bare: false,
            requestId: null,
            // scene: scene,
            sceneDb: this.sceneDb,
            configSession: this.configSession,
            messageCallback: null,
            configuratorLoadedPromise: this.configuratorLoadedDeferred.promise,
            arLoading: this.$scope.arLoading,
            navigateToElement: o => {
                this.navigateToElement(o);
            },
            compileTemplate: (hotspotDiv, scope) => {
                return this.$compile(hotspotDiv)(scope as ng.IScope)[0];
            },
            lastDeploy:
                env == eEnvironment.dev
                    ? 0
                    : env == eEnvironment.test
                    ? company.lastTestDeployment
                    : company.lastProdDeployment,
        } as ISceneViewerArgs;

        if (scene.external) {
            let cArgs = vArgs as IClaraSceneViewerArgs;
            cArgs.sceneService = this.$injector.get(Token.ClaraSceneService.key);
            cArgs.playerUrl = this.$rootScope.context.claraV2LibraryUrl;
            cArgs.ocLazyLoad = this.ocLazyLoad;
            sceneViewer = new ClaraViewer(cArgs);
        } else {
            sceneViewer = new Kb3dViewer(vArgs);
        }

        return sceneViewer.init().then(() => {
            viewerScope.$on('$destroy', () => {
                sceneViewer.dispose();
            });
            return sceneViewer;
        });
    }

    public runSceneAction(config: Configurator, actionName: string): ng.IPromise<any> {
        if (this.lastViewerPromise) {
            return this.lastViewerPromise.then(() => {
                return this.$scope.activeSceneViewer && this.$scope.activeSceneViewer.runAction(actionName);
            });
        } else {
            return this.refreshViewer().then(() => {
                return this.$scope.activeSceneViewer && this.$scope.activeSceneViewer.runAction(actionName);
            });
        }
    }

    protected getCurrentConfigForViewerAndSetViewerMode(): Configurator {
        let currentConfig = this.rootConfig;
        if (this.$scope.selected.page) currentConfig = this.$scope.selected.page.$parentConfigurator;
        // find the closest config that should show a 3d viewer
        while (currentConfig) {
            if (currentConfig.viewerMode == eViewerMode.media || currentConfig.viewerMode == eViewerMode.none) {
                break;
            } else if (currentConfig.viewerMode == eViewerMode.scene && currentConfig.idScene) {
                if (!currentConfig.$parent) break;
                // it's nested: we only show it if it's not set to merge scenes with it's parent
                let refConfig = currentConfig.$parentConfigurator.referencedConfigurators.find(
                    rc => rc.idProduct == currentConfig.idProduct
                );
                if (!refConfig.nestScene) break;
            }

            currentConfig = currentConfig.$parentConfigurator;
        }

        if (!currentConfig) {
            this.$scope.viewerMode = eViewerMode.none;
        } else {
            this.$scope.viewerMode = currentConfig.viewerMode;
        }

        return currentConfig;
    }

    public refreshViewer(skipRules = false): ng.IPromise<any> {
        if (!this.lastViewerPromise) this.lastViewerPromise = new KPromise(null) as any;

        this.lastViewerPromise = this.lastViewerPromise.then(() => {
            let viewerDeferred = this.$q.defer();
            let origSceneViewer = this.$scope.activeSceneViewer;
            let origConfig = origSceneViewer ? origSceneViewer.uiConfig : null;
            let currentConfig = this.getCurrentConfigForViewerAndSetViewerMode();

            if (currentConfig) {
                if (this.sceneDb[currentConfig.idScene] && this.sceneDb[currentConfig.idScene].external) {
                    currentConfig.nestedPath = currentConfig.idScene;
                } else {
                    currentConfig.nestedPath = this.getConfiguratorUniqueKey(currentConfig);
                }

                if (currentConfig.viewerMode == eViewerMode.media) {
                    this.$scope.viewerMediaPath = currentConfig.viewerMediaPath;
                    viewerDeferred.resolve();
                } else if (currentConfig.viewerMode == eViewerMode.scene) {
                    let clear =
                        this.rootConfig.clearMemoryWhenSwitchingScenes &&
                        this.$scope.activeSceneViewer &&
                        this.$scope.activeSceneViewer.scene.id != currentConfig.idScene;

                    /**
                     * for kb3d if we are clearing memory we just dispose of the old viewer and make a new one
                     */
                    if (clear && !this.sceneDb[currentConfig.idScene].external) {
                        delete this.sceneViewerDb[currentConfig.nestedPath];
                        origSceneViewer.dispose();
                        this.$scope.activeSceneViewer = null;
                        clear = false;
                    }

                    if (clear) {
                        this.$timeout(() => {
                            this.sceneViewerDb[currentConfig.nestedPath] = this.$scope.activeSceneViewer;
                            this.$scope.activeSceneViewer
                                .loadScene({
                                    scene: this.sceneDb[currentConfig.idScene],
                                    clearMemory: true,
                                    config: currentConfig,
                                    isStandalone: this.$scope.isScene,
                                    isRenderContext: false,
                                })
                                .then(() => viewerDeferred.resolve());
                        }, 0);
                    } else {
                        this.$scope.activeSceneViewer = this.sceneViewerDb[currentConfig.nestedPath];
                        if (this.$scope.activeSceneViewer) {
                            // execute scene on next frame so configurator appears responsive
                            this.$timeout(() => {
                                // if we are switching configurators, but keeping the same sceneViewer,
                                // then we need to clear the clickDb
                                if (this.$scope.activeSceneViewer.uiConfig != currentConfig) {
                                    this.$scope.activeSceneViewer.clearHandlers();
                                }
                                // set the uiConfig of the viewer
                                if (this.$scope.activeSceneViewer.sceneConfig.idProduct == currentConfig.idProduct) {
                                    currentConfig.$sceneConfigurator = this.$scope.activeSceneViewer.sceneConfig;
                                    this.$scope.activeSceneViewer.sceneConfig.$uiConfigurator = currentConfig;
                                    this.$scope.activeSceneViewer.uiConfig = currentConfig;
                                }
                                //only skip rules if we are told to and we haven't switched scene viewers
                                if (skipRules && this.$scope.activeSceneViewer.uiConfig == origConfig) {
                                    viewerDeferred.resolve();
                                } else {
                                    this.$scope.activeSceneViewer
                                        .runRules(true)
                                        //.then(result => this.helper.handleRuleError(eRuleType.scene, result))
                                        .then(() => viewerDeferred.resolve());
                                }
                            }, 0);
                        } else {
                            // init the sceneViewer which also runs the rules
                            //if there is no viewer in the layout, then we skip
                            if (!this.isViewerInLayout()) {
                                viewerDeferred.resolve();
                            } else {
                                this.waitForViewerReady().then(
                                    () => {
                                        let scene = this.sceneDb[currentConfig.idScene];
                                        this.createSceneViewer(scene).then(sceneViewer => {
                                            this.$scope.activeSceneViewer = sceneViewer;
                                            this.sceneViewerDb[currentConfig.nestedPath] =
                                                this.$scope.activeSceneViewer;
                                            this.$scope.activeSceneViewer
                                                .loadScene({
                                                    scene,
                                                    clearMemory: this.rootConfig.clearMemoryWhenSwitchingScenes,
                                                    config: currentConfig,
                                                    isStandalone: this.$scope.isScene,
                                                    isRenderContext: false,
                                                })
                                                .then(() => viewerDeferred.resolve());
                                        });
                                    },
                                    err => {
                                        viewerDeferred.reject(err);
                                    }
                                );
                            }
                        }
                    }
                } else {
                    viewerDeferred.resolve();
                }
            } else {
                viewerDeferred.resolve();
            }

            // set other viewers to hide
            Object.keys(this.sceneViewerDb).forEach(key => {
                if (
                    this.$scope.viewerMode != eViewerMode.scene ||
                    this.$scope.activeSceneViewer !== this.sceneViewerDb[key]
                ) {
                    this.sceneViewerDb[key].hide();
                } else {
                    this.sceneViewerDb[key].show();
                }
            });

            return viewerDeferred.promise;
        });
        // console.log(this.configuratorMap);
        // console.log(this.nestedSceneMap);
        return this.lastViewerPromise;
    }

    public isViewerInLayout() {
        return this.$scope.config.getChildrenOfType<Viewer>(CToken.Viewer).some(v => v.visible);
    }

    public waitForViewerReady(): Q.IPromise<any> {
        if (!this.isViewerInLayout()) {
            return new KPromise<any>(null);
        } else {
            let elemDefer = Q.defer();
            let waitedFor = 0;
            let wait = 30;
            let check = () => {
                let parentElem = document.querySelector('.kb-viewer') as HTMLElement;
                if (parentElem && parentElem.getBoundingClientRect().height > 0) {
                    if (waitedFor) console.log(`waited ${waitedFor}ms for viewer ready`);
                    elemDefer.resolve();
                } else {
                    setTimeout(() => {
                        waitedFor += wait;
                        check();
                    }, wait);
                }
            };
            check();
            return elemDefer.promise as Q.IPromise<any>;
        }
    }

    public hasPricing() {
        return (
            this.rootConfig.hasPricingRule ||
            this.hasNestedPricing() ||
            (this.$scope.isTest && this.rootConfig.ruleContainers.some(rc => rc.ruleType == eRuleType.pricing))
        );
    }

    public runPricing(): ng.IPromise<any> {
        let promise = new KPromise(null) as any;
        if (this.hasPricing()) {
            let configuredProduct = this.rootConfig.getConfiguredProduct();
            promise = this.lastPricingPromise = this.$http
                .post<IPriceObject>('/api/products/' + this.$scope.entity.id + '/pricing', configuredProduct, {
                    tracker: this.$scope.pricingTracker,
                })
                .then(
                    response => {
                        this.rootConfig.priceObject = this.$scope.priceObject = response.data;
                    },
                    error => {
                        this.helper.handleRuleError('Pricing Rule', {
                            hasError: true,
                            error: new Error(error.data.error),
                            parameters: this.getRuleArgs(this.rootConfig),
                        });
                    }
                );
        }
        return promise;
    }

    public addLevelsToPricing(item: IPriceItemBase, level: number = -1) {
        let o: any = item;
        if (o) {
            o.$level = level;
            item.items && item.items.forEach(i => this.addLevelsToPricing(i, level + 1));
        }
    }

    public hasNestedPricing() {
        return this.rootConfig
            .getNestedConfigurators()
            .some(
                nested =>
                    nested.hasPricingRule &&
                    this.rootConfig.referencedConfigurators.find(rc => rc.idProduct == nested.idProduct).nestPriceObject
            );
    }

    public setScene(configurator: Configurator, idScene: number): ng.IPromise<any> {
        if (configurator.idScene != idScene) {
            configurator.idScene = idScene;
            configurator.$nestedSceneId = null;
            configurator.$sceneConfigurator = null;

            return this.refreshViewer();
        }

        return new KPromise(null) as any;
    }

    public trackConfigPageView(page: Page) {
        if (page) {
            let path = page.name;
            let parent = page.$parentConfigurator;
            while (parent.$parentConfigurator) {
                path = `${parent.name}/${path}`;
                parent = parent.$parentConfigurator;
            }
            this.telemetryService.trackEvent('Configurator Page View', { pageName: path });
        }
    }

    private getParameters() {
        var parameters = Utils.extend(this.$location.search(), this.$scope.embedParameters);

        // combine query string parameters with sfdc parameters
        if (this.$rootScope.context.sfdcCanvasRequest) {
            parameters = Utils.extend(
                this.$rootScope.context.sfdcCanvasRequest.context.environment.parameters,
                parameters
            );
            if (this.$rootScope.isSfdcCpq) {
                parameters.sfdcQuote = this.sfdcCpqPayload.fullQuote;
                parameters.sfdcConfiguredProductId = this.sfdcCpqPayload.data.product.configuredProductId;
                parameters.sfdcQuoteLine = this.sfdcCpqPayload?.data?.product;
            }
        }
        return parameters;
    }

    public scheduledRules: any[];
    public runContinuousAsyncRule(id: string, timeSeconds: number) {
        if (!this.scheduledRules) this.scheduledRules = [];

        
        if (this.scheduledRules.find(a => a.id == id)) {
            this.cancelScheduledRule(id);
        }
        
        let interval = this.$interval(() => {
            if (this.$rootScope && this.runAsyncRule) {
                this.$rootScope.showSpinner = false;
                return this.helper.runActionByIdAsync(this.rootConfig, id).then(result => {
                    this.$rootScope.showSpinner = true;
                    this.$rootScope.$apply();

                    // TODO: Will need to run this conditionally on whether a rule cycle
                    // actually needs to be run after an interval is run.
                    //this.runRuleCycleUnit(this.$scope.config, null);
                });
            }
            
        }, timeSeconds * 1000);

        this.scheduledRules.push({
            id: id,
            promise: interval
        });

    }

    public cancelScheduledRule(id: string) {
        if (this.scheduledRules && this.scheduledRules.length > 0) {
            let runningRule = this.scheduledRules.find(a => a.id == id);
            if (runningRule) {
                this.$interval.cancel(runningRule.promise);
                this.scheduledRules.remove(runningRule);
            }
        }

        return;
    }

    public runTimeoutRule(func: Function, timeSeconds: number) {

        this.$timeout(() => {
            //this.$rootScope.showSpinner = false;
            if (this.$rootScope && this.runAsyncRule) {
                this.$rootScope.showSpinner = false;
                let res = func();
                if (res instanceof Promise) {
                    return res.then(() => {
                        this.$rootScope.showSpinner = true;
                    });
                } else {
                    return res;
                }
                
                // return this.helper.runActionByIdAsync(this.rootConfig, id).then(() => {
                //     this.$rootScope.showSpinner = true;
                //     this.$rootScope.$apply();
                // });
            }

        }, timeSeconds * 1000);
    }

    protected fillSourcedTables(session: IConfiguratorSession): ng.IPromise<any> {
        let tablePromise: ng.IPromise<ITable[]> = new KPromise(null) as any;
        if (session.sourcedTableIds && session.sourcedTableIds.length) {
            return this.api.tables.getBatch({ ids: session.sourcedTableIds }).then(sourcedTables => {
                if (sourcedTables && sourcedTables.length) {
                    session.tables.pushArray(sourcedTables);
                }
            });
        } else {
            return new KPromise(null) as any;
        }
    }
}
